/**
 * The volumes an Ember humanoid is drawn from, hung on a solved skeleton.
 *
 * Clothing is geometry, not a painted layer: a garment re-uses the body's own volumes, slightly
 * inflated, in its own material. The raster keeps whichever surface is nearest the camera, so a
 * sleeve covers the arm, the arm passes in front of the shirt, and a cape falls behind the back,
 * frame by frame and facing by facing, with no layer order to get wrong. A new pose costs nothing
 * per garment.
 */
import { BONE, BONES, Material, SKIN, dye, hairDye, metalFrom, rampFrom } from "./palette"
import type { Facing } from "./raster"
import { DEFAULT_RASTER, FACING_YAW } from "./raster"
import { LimbJoints, Proportions, SIDES, Side, Skeleton, farSide, legLength, sideSign } from "./rig"
import { Mat3, Vec3, add, apply, cross, deg, distance, dot, lerp, normalize, rotY, scale, sub, transpose } from "./vec"

/** Which body part a pixel belongs to. Edges between parts are drawn as creases. */
/** `skirt`: one garment round both legs, shaded as torso cloth but outlined against the legs. */
export type PartGroup = "head" | "hair" | "torso" | "bust" | "shoulder" | "armL" | "armR" | "legL" | "legR" | "held" | "skirt" | "tail"

interface PrimitiveBase {
    material: Material
    group: PartGroup
    /**
     * Which segment of its limb this is. Where two segments of one limb meet -- elbow, wrist, knee,
     * ankle -- a line is drawn, as LPC draws every joint; within a segment, none.
     */
    part?: string
    /** Optional colour painted onto the surface, from the hit point in the primitive's own frame. */
    decal?: (local: Vec3) => number | undefined
}

export interface Capsule extends PrimitiveBase {
    kind: "capsule"
    a: Vec3
    b: Vec3
    r: number
}

export interface Ellipsoid extends PrimitiveBase {
    kind: "ellipsoid"
    center: Vec3
    /** Columns are the ellipsoid's local x, y, z axes. */
    basis: Mat3
    radii: Vec3
    /**
     * Squareness: 2 is an ellipsoid; higher flattens toward a box with rounded edges
     * (|x/a|^ex + |y/b|^ey + |z/c|^ez = 1), one number for all axes or one per axis. LPC heads and
     * chests are flat-sided from the front but round in profile: square across, round front to back.
     */
    exponent?: number | Vec3
    /** Keep only the parts of the shell for which this holds (local, unscaled coordinates). */
    keep?: (local: Vec3) => boolean
}

export type Primitive = Capsule | Ellipsoid

export type Sleeves = "none" | "short" | "long"
/** Ember's own hair styles; a look may also name any of the game's (LPC's) styles (HAIR_DESIGNS). */
export type HairStyle = "bald" | "crop" | "short" | "long" | "ponytail" | string

/** What an Ember humanoid wears and looks like. Data, so a new character is a new record. */
export interface EmberLook {
    skin: keyof typeof SKIN | string
    /** A skeleton: bones where the flesh would be (bonesOf), a skull without eyes or hair. */
    bones?: boolean
    /** Its bone's colour, a BONES name (`green`): the game's skeleton body colour. Ivory unless named. */
    boneColor?: string
    /** Torn flesh: open wounds painted over the body (woundsOf), as LPC's zombie has. */
    wounds?: boolean
    /**
     * A beast on the man's rig: `wolf`, LPC's wolfman -- a wolf's head (wolfHeadOf), a ruff of fur at
     * the neck and a tail, fur for skin (palette `wolf-<colour>`), and no hair.
     */
    beast?: "wolf"
    /** An EYE_STYLES name (face.ts); Ember's own by default. */
    eyes?: string
    hair?: { style: HairStyle; color: string }
    /**
     * A shirt: its sleeves, `tunic` over the hips (with `hem: "thigh"` a skirt to mid-thigh), and
     * what shirtOf adds -- a neckline, puffed shoulders, a pattern.
     */
    shirt?: {
        dye: string
        sleeves: Sleeves
        tunic?: boolean
        hem?: "thigh"
        /** `scoop` a round neck, `low` a deep one with the shoulders bare but for straps, `laced` a laced V. */
        neck?: "scoop" | "low" | "laced"
        puffed?: boolean
        pattern?: "stripes" | "lacing"
    }
    /**
     * Leg wear, `trousers` unless its `cut` says otherwise: `shorts` to the knee, `pantaloons` loose
     * and gathered below the calf (pantaloonsOf), a `skirt` round both legs (skirtOf), a legionary's
     * strapped `legion` skirt (legionOf), steel `greaves` (greavesOf; `dye` names a metal).
     */
    pants?: { dye: string; cut?: PantsCut }
    /** Footwear, `shoes` unless its `cut` says otherwise (footwearOf); sabatons name a metal. */
    shoes?: { dye: string; cut?: ShoeCut }
    belt?: { dye: string }
    /** Body armour over the shirt (armorOf): its kind and colour. Equipment, like `held`. */
    armor?: { kind: ArmorKind; color: string }
    /**
     * The game's bauldron (bauldronOf): a leather pauldron on the right shoulder, strapped across the
     * chest to the left side, over whatever is worn there. Equipment, like `held`.
     */
    bauldron?: { color: string }
    /**
     * A shield (shieldOf): the game's shield item type and colour, in the left fist, or `slung` on
     * the back while that hand has a bow. Equipment, like `held`.
     */
    shield?: { type: string; color: string; slung?: boolean }
    /** What the right hand holds: an EMBER_HELD name (`axe`). Equipment, not a fixed part of the look. */
    held?: string
    /** The held item's colour variant, as the item names it (`metal`, `brown`). */
    heldColor?: string
}

const capsule = (a: Vec3, b: Vec3, r: number, material: Material, group: PartGroup): Capsule => ({
    kind: "capsule",
    a,
    b,
    r,
    material,
    group,
})

const ellipsoid = (
    center: Vec3,
    basis: Mat3,
    radii: Vec3,
    material: Material,
    group: PartGroup,
    extra: Partial<Ellipsoid> = {},
): Ellipsoid => ({ kind: "ellipsoid", center, basis, radii, material, group, ...extra })

const inflate = (r: Vec3, by: number): Vec3 => [r[0] + by, r[1] + by, r[2] + by]

/**
 * A limb segment shaped like muscle rather than pipe: an ellipsoid along the bone, fullest in the
 * middle and narrowing toward both joints, where a smaller ball (`joint`) keeps the bend filled.
 * A tube of constant radius reads as a mannequin; this reads as an upper arm, a calf.
 */
const segment = (a: Vec3, b: Vec3, r: number, m: Material, group: PartGroup, exponent?: number): Ellipsoid => {
    const along = sub(b, a)
    const half = Math.sqrt(dot(along, along)) / 2
    const y = normalize(along)
    const x = normalize(Math.abs(y[2]) < 0.9 ? cross(y, [0, 0, 1]) : cross(y, [1, 0, 0]))
    const z = cross(x, y)
    const basis: Mat3 = [x[0], y[0], z[0], x[1], y[1], z[1], x[2], y[2], z[2]]
    return ellipsoid(scale(add(a, b), 0.5), basis, [r, half + r * 0.45, r], m, group, exponent ? { exponent } : {})
}
/**
 * A foot: an ellipsoid from heel to toe, as tall as `r` and `width` across, so from the front it is
 * the broad wedge LPC draws below the ankle rather than a peg the width of the shin.
 */
const foot = (heel: Vec3, toe: Vec3, r: number, width: number, m: Material, group: PartGroup): Ellipsoid => {
    const along = sub(toe, heel)
    const half = Math.sqrt(dot(along, along)) / 2
    const z = normalize(along)
    const x = normalize(cross([0, 1, 0], z))
    const y = cross(z, x)
    const basis: Mat3 = [x[0], y[0], z[0], x[1], y[1], z[1], x[2], y[2], z[2]]
    // Squared across and underneath: a flat sole, not a rocker.
    return ellipsoid(scale(add(heel, toe), 0.5), basis, [width, r, half + r], m, group, { exponent: [3, 3, 2.2] })
}
const joint = (at: Vec3, r: number, m: Material, group: PartGroup): Ellipsoid =>
    ellipsoid(at, [1, 0, 0, 0, 1, 0, 0, 0, 1], [r, r, r], m, group)

/** Name the limb segment a primitive is (see PrimitiveBase.part). */
const as = <T extends Primitive>(prim: T, part: string): T => ({ ...prim, part })

/** Which arm/leg group a side's limbs belong to. */
const armOf = (side: "L" | "R"): PartGroup => (side === "L" ? "armL" : "armR")
const legOf = (side: "L" | "R"): PartGroup => (side === "L" ? "legL" : "legR")

/**
 * A frame square to a leg bone from `a` to `b`: `along` it, `ahead` the way its foot points, `out`
 * away from the other leg; `basis` is (x, along, ahead) for ellipsoids laid along the bone.
 */
const legFrame = (sk: Skeleton, side: Side, a: Vec3, b: Vec3) => {
    const along = normalize(sub(b, a))
    const square = (v: Vec3) => sub(v, scale(along, dot(v, along)))
    const toe = square(sub(sk[side].toe, sk[side].heel))
    const ahead = normalize(dot(toe, toe) > 1e-4 ? toe : square(apply(sk.pelvisBasis, [0, 0, 1])))
    const x = cross(along, ahead)
    const basis: Mat3 = [x[0], along[0], ahead[0], x[1], along[1], ahead[1], x[2], along[2], ahead[2]]
    const out = scale(x, dot(x, apply(sk.pelvisBasis, [sideSign(side), 0, 0])) < 0 ? -1 : 1)
    return { along, ahead, out, basis, length: distance(a, b) }
}

/**
 * A thigh, `by` off the skin (garments pass how far they stand off it): one muscle-shaped segment,
 * and on an athlete's legs (`quad`) the quadriceps swelling it in front and to the outside, fullest
 * a third of the way down and drawn in to the knee -- a thigh that reads strong, not just thick.
 */
export const TUNE_LEG = { quadDown: 0.38, quadAhead: 0.3, quadOut: 0.15, quadWidth: 1, quadLength: 0.4, quadDepth: 0.9, calfBack: 0.45, calfLength: 0.28, calfWidth: 1, calfDepth: 0.85, heads: [[0.3, 0.3, 0.9], [0.36, -0.35, 1]] }
const thighOf = (sk: Skeleton, side: Side, p: Proportions, by: number, m: Material, g: PartGroup): Ellipsoid[] => {
    const j = sk[side]
    const prims = [segment(j.hip, j.knee, p.thighRadius + by, m, g)]
    if (p.quad) {
        const f = legFrame(sk, side, j.hip, j.knee)
        const L = TUNE_LEG
        const at = add(lerp(j.hip, j.knee, L.quadDown), add(scale(f.ahead, p.thighRadius * L.quadAhead), scale(f.out, p.thighRadius * L.quadOut)))
        prims.push(ellipsoid(at, f.basis, [p.quad * L.quadWidth + by, f.length * L.quadLength + by, p.quad * L.quadDepth + by], m, g))
    }
    return prims
}

/**
 * A shin from the knee to `to` of the way to the ankle, `by` off the skin: one segment, and on an
 * athlete's legs (`calf`) the two heads of the calf behind its upper third -- the inner one fuller
 * and lower -- falling away to a slim ankle.
 */
const shinOf = (sk: Skeleton, side: Side, p: Proportions, by: number, m: Material, g: PartGroup, to = 1): Ellipsoid[] => {
    const j = sk[side]
    const prims = [segment(j.knee, to === 1 ? j.ankle : lerp(j.knee, j.ankle, to), p.shinRadius + by, m, g)]
    if (p.calf) {
        const f = legFrame(sk, side, j.knee, j.ankle)
        const L = TUNE_LEG
        const back = scale(f.ahead, -p.shinRadius * L.calfBack)
        for (const [down, outward, size] of L.heads) {
            const at = add(lerp(j.knee, j.ankle, down), add(back, scale(f.out, outward * p.shinRadius)))
            prims.push(ellipsoid(at, f.basis, [p.calf * size * L.calfWidth + by, f.length * L.calfLength + by, p.calf * size * L.calfDepth + by], m, g))
        }
    }
    return prims
}

/**
 * The skull: a rounded box, flat at the sides and across the face, widest at the cheeks -- the
 * shape the LPC head is drawn as -- with ears standing a pixel proud at eye level. The eyes are
 * not geometry: they are stamped on as LPC pixels (face.ts).
 */
const head = (sk: Skeleton, p: Proportions, skin: Material): Primitive[] => {
    const [a, b, c] = p.head
    const skull = add(sk.head, apply(sk.headBasis, [0, p.craniumLift, 0]))
    const prims: Primitive[] = [ellipsoid(skull, sk.headBasis, p.head, skin, "head", { exponent: p.headRoundness })]
    // The jaw: the lower face, forward and below the skull's base, so the chin is the lowest point
    // of the head and the line under the jaw slants back up to the neck. Tall and flat-fronted, so in
    // profile the face runs straight down from the brow to the chin, as LPC draws it, and only then
    // turns back under the jaw.
    const jaw = add(sk.head, apply(sk.headBasis, [0, -b * 0.5, c * 0.38]))
    // As wide as the cheeks, so from the front the face keeps the skull's outline to the chin.
    prims.push(ellipsoid(jaw, sk.headBasis, [a * 0.9, b * 0.62, c * 0.6], skin, "head", { exponent: [2.6, 3, 2.0] }))
    // Ears sized and set for the LPC head (10.05 x 8.7 x 9.15), scaled to this one's.
    const [kx, ky, kz] = [a / 10.05, b / 8.7, c / 9.15]
    for (const s of [1, -1]) {
        const ear = add(sk.head, apply(sk.headBasis, [s * (a - 0.5 * kx), p.earHeight, -0.8 * kz]))
        prims.push(ellipsoid(ear, sk.headBasis, [1.6 * kx, 2.3 * ky, 1.5 * kz], skin, "head"))
    }
    return prims
}

const WOLF_NOSE = rampFrom("#2a1f22", "#0c0809")
const WOLF_EAR = rampFrom("#c98a86")

/**
 * A wolf's head on the man's neck, as LPC's wolfman wears one: the skull, a long muzzle reaching
 * well past the face with a dark nose on its tip and a lower jaw under it, the fur of the jowls
 * filling out the cheeks, and tall pointed ears standing up off the crown, pink inside. Sized off
 * the man's own head (p.head), so the eyes (face.ts, the `wolf` style) still sit where his would.
 */
const wolfHeadOf = (sk: Skeleton, p: Proportions, fur: Material, facing: Facing): Primitive[] => {
    const [a, b, c] = p.head
    const at = (v: Vec3) => add(sk.head, apply(sk.headBasis, v))
    // The muzzle a shade darker than the coat, as LPC paints it: front-on, it is what makes the face a wolf's.
    const muzzle: Material = { ...fur, light: fur.base, base: fur.shade, shade: fur.deep, deep: fur.line }
    // The muzzle's dark runs on up the bridge between the eyes to the brow.
    const lit = lighting(facing)
    const toHead = transpose(sk.headBasis)
    const skull = painted([ellipsoid(at([0, p.craniumLift * 0.5, -c * 0.05]), sk.headBasis, [a * 0.95, b * 0.88, c * 0.95], fur, "head", { exponent: p.headRoundness })],
        (w, n) => {
            const q = apply(toHead, sub(w, sk.head))
            const width = a * (0.22 - 0.1 * Math.max(0, q[1] / b))
            return q[2] > c * 0.3 && Math.abs(q[0]) < width && q[1] > -b * 0.3 && q[1] < b * 0.45 ? tone(muzzle, lit(n)) : undefined
        })
    const prims: Primitive[] = [
        ...skull,
        // The muzzle, square-ish in section, its tip well ahead of where a man's face would be.
        ellipsoid(at([0, -b * 0.32, c * 1.0]), sk.headBasis, [a * 0.42, b * 0.38, c * 0.8], muzzle, "head", { exponent: [2.2, 2.6, 2] }),
        ellipsoid(at([0, -b * 0.62, c * 0.72]), sk.headBasis, [a * 0.36, b * 0.22, c * 0.6], muzzle, "head"),
        ellipsoid(at([0, -b * 0.16, c * 1.8]), sk.headBasis, [Math.max(0.9, a * 0.22), Math.max(0.8, b * 0.18), 0.8], WOLF_NOSE, "head"),
    ]
    for (const s of [1, -1]) prims.push(ellipsoid(at([s * a * 0.7, -b * 0.4, -c * 0.1]), sk.headBasis, [a * 0.36, b * 0.48, c * 0.6], fur, "head"))
    // The ears: leaves standing up and a little out from the crown, their fronts pink.
    const ahead = apply(sk.headBasis, [0, 0, 1])
    const ears = [1, -1].map(s => segment(at([s * a * 0.5, b * 0.5, -c * 0.1]), at([s * a * 0.95, b * 1.6, -c * 0.3]), Math.max(1.3, a * 0.34), fur, "head"))
    prims.push(...painted(ears, (_w, n) => (dot(n, ahead) > 0.4 ? tone(WOLF_EAR, lit(n)) : undefined)))
    return prims
}

/**
 * The rest of a wolfman's wolf: a thick ruff of fur round the neck, setting the head down into the
 * shoulders as LPC draws it, and a bushy tail from the base of the spine, outlined as a part of its own.
 */
const wolfFurOf = (sk: Skeleton, p: Proportions, fur: Material): Primitive[] => {
    const leg = legLength(p)
    const ruff = ellipsoid(add(sk.neck, apply(sk.torsoBasis, [0, -p.neckRadius * 0.3, -p.neckRadius * 0.2])), sk.torsoBasis,
        [p.neckRadius * 2.0, p.neckRadius * 1.35, p.neckRadius * 1.75], fur, "torso")
    const root = add(sk.pelvis, apply(sk.pelvisBasis, [0, -p.pelvis[1] * 0.15, -p.pelvis[2] * 0.85]))
    const mid = add(root, apply(sk.pelvisBasis, [0, -leg * 0.2, -leg * 0.14]))
    const tip = add(root, apply(sk.pelvisBasis, [0, -leg * 0.46, -leg * 0.16]))
    const r = Math.max(1.3, p.thighRadius * 0.45)
    return [ruff, segment(root, mid, r, fur, "tail"), segment(mid, tip, r * 1.35, fur, "tail")]
}

/**
 * Chest, shoulders and neck. The shoulder girdle is the top of a wide dome, so the line from the
 * neck to each deltoid SLOPES, and each deltoid caps its arm round: an arm is hung under a
 * shoulder, not bolted to the corner of a box. `by` inflates every volume (garments).
 */
const upperBody = (sk: Skeleton, p: Proportions, m: Material, by = 0, facing: Facing = "front"): Primitive[] => {
    // The chest carried a little forward of the spine: upright, chest out, not a straight post.
    const chest = add(sk.chest, apply(sk.torsoBasis, [0, 0, p.posture]))
    const prims: Primitive[] = [
        ellipsoid(chest, sk.torsoBasis, inflate(p.torso, by), m, "torso", { exponent: p.torsoRoundness }),
    ]
    // A garment's girdle grows out and around but not UP: lowered by what it is inflated by, its top
    // stays at the skin's, so a collar sits under the chin instead of riding over the face.
    const girdle = add(sk.chest, apply(sk.torsoBasis, [0, p.girdle.drop - by, -0.3]))
    const cut = p.girdle.cut
    prims.push(
        ellipsoid(girdle, sk.torsoBasis, inflate(p.girdle.radii, by), m, "torso", {
            keep: q => q[1] > cut,
            exponent: p.girdle.exponent,
        }),
    )
    for (const side of SIDES) {
        // Side-on, the far deltoid would show past the throat, under the chin, where LPC leaves a
        // notch between jaw and chest: three-quarters on, it is drawn hidden behind the chest.
        if ((facing === "left" || facing === "right") && side === farSide(facing)) continue
        const s = sideSign(side)
        const cap = add(sk[side].shoulder, apply(sk.torsoBasis, [s * 0.3, 0.5, 0]))
        const r = p.deltoidRadius + by
        // Its own part: the shoulder is where the arm flows into the chest, outlined against neither.
        prims.push(ellipsoid(cap, sk.torsoBasis, [r, r, r], m, "shoulder"))
    }
    if (p.pecs) {
        // Pectorals: wide, flat slabs across the upper chest either side of the breastbone.
        for (const s of [1, -1]) {
            const at = add(chest, apply(sk.torsoBasis, [s * p.torso[0] * 0.36, p.torso[1] * 0.22, p.torso[2] * 0.72]))
            prims.push(ellipsoid(at, sk.torsoBasis, [p.pecs * 1.25 + by, p.pecs * 0.8 + by, p.pecs * 0.62 + by], m, "torso", { exponent: [2.4, 2.2, 2] }))
        }
    }
    if (p.bust) {
        // A woman's bust, on the chest's front below the collarbones: part of the chest, so a
        // shirt or a breastplate (this same upperBody, inflated) covers it. A part of its own, so
        // where it stands out from the chest -- under it -- the raster draws the seam line LPC
        // draws there: front-on, that line is what shows it.
        for (const s of [1, -1]) {
            // Far enough forward to stand proud of the chest's front: a profile shows it.
            // Set forward and low enough that its underside overhangs the ribcage turning back under
            // it: there it stands far enough proud for the seam line below it.
            const at = add(chest, apply(sk.torsoBasis, [s * p.torso[0] * 0.47, -p.torso[1] * 0.2, p.torso[2] * 1.0]))
            prims.push(ellipsoid(at, sk.torsoBasis, [p.bust + by, p.bust * 0.9 + by, p.bust * 0.85 + by], m, "bust"))
        }
    }
    return prims
}

/**
 * Hips: the pelvis, and a woman's waist above it. `legWear` stops at the natural waist -- the waist
 * volume's middle -- as trousers and skirts do, instead of riding up the ribs.
 */
const lowerBody = (sk: Skeleton, p: Proportions, m: Material, by = 0, keep?: (q: Vec3) => boolean, legWear = false): Primitive[] => {
    const prims: Primitive[] = [
        ellipsoid(p.pelvisLow ? add(sk.pelvis, apply(sk.pelvisBasis, [0, -p.pelvisLow, 0])) : sk.pelvis, sk.pelvisBasis, inflate(p.pelvis, by), m, "torso", {
            exponent: p.pelvisRoundness,
            keep,
        }),
    ]
    if (p.waist) {
        // The waist volume shares the pelvis's frame; a garment's `keep` (its top edge) is in the
        // pelvis's frame too, so test it there.
        const lift = p.waistHeight ?? 0, low = p.pelvisLow ?? 0
        const garment = keep && ((q: Vec3) => keep([q[0], q[1] + lift + low, q[2]]))
        const waistKeep = legWear ? (q: Vec3) => q[1] < 0 && (!garment || garment(q)) : garment
        prims.push(ellipsoid(add(sk.pelvis, apply(sk.pelvisBasis, [0, lift, 0])), sk.pelvisBasis, inflate(p.waist, by), m, "torso", {
            keep: waistKeep,
        }))
    }
    return prims
}

/** The middle of a closed hand: where a held tool passes through it. */
export const fistCenter = (sk: Skeleton, p: Proportions, side: Side): Vec3 => {
    const j = sk[side]
    return p.handLength
        ? add(j.hand, scale(normalize(sub(j.hand, j.elbow)), p.handLength / 2))
        : add(j.hand, apply(sk.torsoBasis, [-sideSign(side) * p.handInset, 0, 0]))
}

const AXE_HAFT = rampFrom("#8a5b34")
const AXE_HEAD = rampFrom("#a3acb5")

/**
 * A woodsman's axe in the fist: a haft through the hand along its grip, the iron head at the far
 * end with its edge on the forearm's side of the haft -- the side that leads when the arm chops.
 * Sized to the body that holds it, so the same axe fits an LPC-sized or a full-sized man.
 */
const axe = (j: LimbJoints, fist: Vec3, p: Proportions, _color?: string): Primitive[] => {
    const leg = legLength(p)
    const d = normalize(j.grip)
    const forearm = normalize(sub(j.hand, j.elbow))
    const across = sub(forearm, scale(d, dot(forearm, d)))
    const edge = dot(across, across) > 1e-6 ? normalize(across) : normalize(cross(d, [1, 0, 0]))
    const len = leg * 0.93
    const r = Math.max(0.8, leg * 0.042)
    const butt = add(fist, scale(d, -len * 0.12))
    const tip = add(fist, scale(d, len * 0.88))
    // The head's frame: thin across the swing, tall along the haft, long out to the edge.
    const thin = normalize(cross(edge, d))
    const basis: Mat3 = [thin[0], d[0], edge[0], thin[1], d[1], edge[1], thin[2], d[2], edge[2]]
    const bladeAt = add(add(tip, scale(d, -len * 0.1)), scale(edge, len * 0.09))
    const pollAt = add(add(tip, scale(d, -len * 0.09)), scale(edge, -len * 0.05))
    return [
        capsule(butt, tip, r, AXE_HAFT, "held"),
        ellipsoid(bladeAt, basis, [r * 0.9, len * 0.15, len * 0.16], AXE_HEAD, "held", { exponent: [2, 2.6, 2.2] }),
        ellipsoid(pollAt, basis, [r * 1.1, len * 0.06, len * 0.06], AXE_HEAD, "held", { exponent: 2.6 }),
    ]
}

const SWORD_STEEL = rampFrom("#c3cbd3", "#4b525e")
const SWORD_BRASS = rampFrom("#b8924a")
const SWORD_GRIP = rampFrom("#4a2e22")

/**
 * A blade from `base` along `d`: overlapping flat ellipsoids, `width` across at the base narrowing
 * to `point` at the tip, `thick` through the flat. `curve` bows it back off the edge (a saber's
 * sweep), as a share of its length at the tip.
 */
const blade = (base: Vec3, d: Vec3, edge: Vec3, thin: Vec3, length: number, width: number, point: number,
    thick: number, curve = 0, steel: Material = SWORD_STEEL): Primitive[] => {
    const at = (u: number): Vec3 => add(add(base, scale(d, u * length)), scale(edge, -curve * length * u * u))
    const prims: Primitive[] = []
    const N = 8
    for (let i = 0; i < N; i++) {
        const u0 = i / N, u1 = (i + 1) / N
        const a = at(u0), b = at(u1)
        const along = normalize(sub(b, a))
        const across = normalize(cross(thin, along))
        const basis: Mat3 = [thin[0], along[0], across[0], thin[1], along[1], across[1], thin[2], along[2], across[2]]
        const half = Math.sqrt(dot(sub(b, a), sub(b, a))) / 2
        // Full width most of the way, then drawn in to the point over the last third.
        const u = (u0 + u1) / 2
        const w = u < 0.65 ? width + (point - width) * 0.25 * (u / 0.65) : width * 0.75 + (point - width * 0.75) * ((u - 0.65) / 0.35)
        prims.push(ellipsoid(scale(add(a, b), 0.5), basis, [thick, half * 1.35, w], steel, "held"))
    }
    return prims
}

const DAGGER_GRIP = rampFrom("#5b3a26")
const DAGGER_GUARD = rampFrom("#8d7a55")
/** The blade by the item's colour: `metal` is steel, `brown` bronze. */
const DAGGER_BLADE: Record<string, Material> = { metal: rampFrom("#b9c2cb", "#4b525e"), brown: rampFrom("#b5824a", "#5a3a22") }

/**
 * A dagger in the fist: a short handle, a small crossguard and a blade two-fifths of the leg long,
 * a long knife on a full-sized man and still a few pixels to read.
 */
/**
 * Which way a blade runs from the fist. A blade sits lower in the hand than an axe's haft stands:
 * halfway between the grip and the forearm's line, so at rest it points forward-down past the
 * thigh and in a cut it runs on straight from the arm.
 */
export const bladeAxis = (j: LimbJoints): Vec3 => normalize(add(normalize(j.grip), normalize(sub(j.hand, j.elbow))))

/**
 * A blade's frame from its axis `d`: `edge` lies across the blade's width, in the plane the wrist
 * bends in (rig LimbJoints.edge), `thin` through its flat.
 */
const bladeFrame = (j: LimbJoints, d: Vec3) => {
    const across = sub(j.edge, scale(d, dot(j.edge, d)))
    const edge = dot(across, across) > 1e-6 ? normalize(across) : normalize(cross(d, [1, 0, 0]))
    const thin = normalize(cross(edge, d))
    const basis: Mat3 = [thin[0], d[0], edge[0], thin[1], d[1], edge[1], thin[2], d[2], edge[2]]
    return { edge, thin, basis }
}

const dagger = (j: LimbJoints, fist: Vec3, p: Proportions, color?: string): Primitive[] => {
    const leg = legLength(p)
    const d = bladeAxis(j)
    const r = Math.max(0.9, leg * 0.032)
    const pommel = add(fist, scale(d, -leg * 0.09))
    const guardAt = add(fist, scale(d, leg * 0.07))
    // The guard runs across the blade's flat, square to the grip.
    const { edge, thin, basis } = bladeFrame(j, d)
    const steel = DAGGER_BLADE[color ?? "metal"] ?? DAGGER_BLADE.metal
    return [
        capsule(pommel, guardAt, r, DAGGER_GRIP, "held"),
        ellipsoid(guardAt, basis, [r * 1.2, r, leg * 0.09], DAGGER_GUARD, "held"),
        ...blade(guardAt, d, edge, thin, leg * 0.42, Math.max(1.8, leg * 0.07), 0.6, Math.max(1.1, leg * 0.04), 0, steel),
    ]
}

/**
 * Which way a sword's blade runs from the fist: along the grip, as the axe's haft does -- carried,
 * up and ahead of the fist, as LPC carries its longsword; in a cut, wherever the wrist turns it.
 */
export const swordAxis = (j: LimbJoints): Vec3 => normalize(j.grip)

/** How far behind the sword hand's fist the other hand takes a two-handed grip. */
export const twoHandSpan = (p: Proportions): number => p.handRadius * 2

/**
 * A longsword: a straight double-edged blade about a leg long (never under 24px, so a short-legged
 * LPC-sized body still holds a sword, not a knife), a cross of brass quillons, and a
 * grip long enough for both hands -- the right by the cross, the left behind it at the pommel's
 * end (clips put it there) -- capped by a wheel pommel.
 */
const longsword = (j: LimbJoints, fist: Vec3, p: Proportions): Primitive[] => {
    const leg = legLength(p)
    const d = swordAxis(j)
    const { edge, thin, basis } = bladeFrame(j, d)
    const guardAt = add(fist, scale(d, p.handRadius + 0.3))
    const pommelAt = add(fist, scale(d, -(twoHandSpan(p) + p.handRadius + 0.4)))
    const quillon = Math.max(3.6, leg * 0.15)
    return [
        ...blade(guardAt, d, edge, thin, Math.max(24, leg * 0.95), Math.max(2.2, leg * 0.085), 0.7, Math.max(1.4, leg * 0.05)),
        capsule(add(guardAt, scale(edge, -quillon)), add(guardAt, scale(edge, quillon)), Math.max(1.0, leg * 0.04), SWORD_BRASS, "held"),
        capsule(guardAt, pommelAt, Math.max(1.0, leg * 0.036), SWORD_GRIP, "held"),
        ellipsoid(pommelAt, basis, [1.3, 1.4, 1.8], SWORD_BRASS, "held"),
    ]
}

/**
 * A cavalry saber: a single-edged blade a little shorter than the longsword (never under 20px),
 * sweeping back off its edge toward the point, a short grip for one hand, and a brass knuckle-bow
 * running from the guard round the front of the fist to the pommel cap.
 */
const saber = (j: LimbJoints, fist: Vec3, p: Proportions): Primitive[] => {
    const leg = legLength(p)
    const d = swordAxis(j)
    const { edge, thin } = bladeFrame(j, d)
    const guardAt = add(fist, scale(d, p.handRadius + 0.2))
    const capAt = add(fist, scale(d, -(p.handRadius + 0.3)))
    const prims = blade(guardAt, d, edge, thin, Math.max(20, leg * 0.85), Math.max(2.0, leg * 0.075), 0.6, Math.max(1.2, leg * 0.045), 0.12)
    // The knuckle-bow: out from the guard on the edge's side, round the fingers, in to the cap.
    const bow = (u: number): Vec3 => add(add(guardAt, scale(d, -u * (p.handRadius * 2 + 0.5))),
        scale(edge, (p.handRadius + 0.6) * Math.sin(Math.PI * Math.min(1, u * 1.15)) + 0.6))
    const r = Math.max(0.8, leg * 0.03)
    for (let i = 0; i < 4; i++) prims.push(capsule(bow(i / 4), bow((i + 1) / 4), r, SWORD_BRASS, "held"))
    prims.push(
        capsule(add(guardAt, scale(edge, -1.2)), add(guardAt, scale(edge, 1.2)), Math.max(0.9, leg * 0.034), SWORD_BRASS, "held"),
        ellipsoid(capAt, [1, 0, 0, 0, 1, 0, 0, 0, 1], [1.2, 1.2, 1.2], SWORD_BRASS, "held"),
    )
    return prims
}

const BOW_WOOD = rampFrom("#a8692f")
const BOW_GRIP = rampFrom("#4a3022")
const BOW_STRING = rampFrom("#ddd5c2")
const ARROW_SHAFT = rampFrom("#c29a68")
const ARROW_HEAD = rampFrom("#a9b2ba")
const ARROW_FLETCH = rampFrom("#b0443a")

/**
 * A longbow in the left hand, about as tall as the man: its limbs run along the hand's grip and
 * curve back toward the archer at the tips, further the harder it is drawn. Undrawn, the string
 * runs straight tip to tip; drawn (the skeleton's `draw`), it runs from each tip to the right hand
 * at the jaw, with an arrow nocked there and laid across the bow hand toward the target.
 */
const bow = (sk: Skeleton, p: Proportions): Primitive[] => {
    const leg = legLength(p)
    const draw = sk.draw ?? 0
    const grip = fistCenter(sk, p, "L")
    const nock = fistCenter(sk, p, "R")
    // Carried, a longbow stands nearly upright in the hand whatever the wrist does; drawn, the
    // archer holds it upright on purpose (the shot's grip): biased to vertical either way.
    const axis = normalize(add(normalize(sk.L.grip), [0, 1.5, 0]))
    // Which way the bow faces: at the nock's opposite side when drawn, else the body's forward.
    const facing = draw > 0 ? sub(grip, nock) : apply(sk.pelvisBasis, [0, 0, 1])
    const ahead0 = sub(facing, scale(axis, dot(facing, axis)))
    const ahead = dot(ahead0, ahead0) > 1e-6 ? normalize(ahead0) : normalize(cross(axis, [1, 0, 0]))
    const half = leg * 0.85
    const bend = 0.1 + 0.22 * draw
    // Far thicker than a real stave (which would be a pixel at this scale): wide enough at the
    // hand that the wood shows inside its outline, narrowing toward the tips as a longbow does.
    const r0 = Math.max(1.9, leg * 0.07)
    const at = (u: number): Vec3 => add(add(grip, scale(axis, u * half * (1 - 0.15 * bend * u * u))), scale(ahead, -bend * half * u * u))
    const prims: Primitive[] = []
    const N = 12
    for (let i = 0; i < N; i++) {
        const u0 = -1 + (2 * i) / N, u1 = -1 + (2 * (i + 1)) / N
        const mid = (u0 + u1) / 2
        const material = Math.abs(mid) < 0.12 ? BOW_GRIP : BOW_WOOD
        prims.push(capsule(at(u0), at(u1), r0 * (1 - 0.4 * mid * mid), material, "held"))
    }
    const top = at(1), bottom = at(-1)
    const string = 0.5
    if (draw > 0) {
        prims.push(capsule(top, nock, string, BOW_STRING, "held"), capsule(nock, bottom, string, BOW_STRING, "held"))
        // The arrow, nocked at the string and laid across the bow hand.
        const flight = normalize(sub(grip, nock))
        const tip = add(nock, scale(flight, leg * 0.82))
        prims.push(capsule(nock, tip, 0.42, ARROW_SHAFT, "held"))
        prims.push(ellipsoid(tip, sk.torsoBasis, [0.7, 0.7, 0.7], ARROW_HEAD, "held"))
        prims.push(capsule(nock, add(nock, scale(flight, leg * 0.1)), 0.7, ARROW_FLETCH, "held"))
    } else {
        prims.push(capsule(top, bottom, string, BOW_STRING, "held"))
    }
    return prims
}

// ---- the rest of the game's weapons ---------------------------------------------------------
// Each a few strong shapes, as LPC draws them, sized off the leg like the axe so the same weapon
// fits any body. Polearms run along the right hand's grip with their butt behind the fist; the clips
// that carry them stand them upright (clips.ts poleCarry), the two-handed swings and thrusts put the
// left hand on the shaft.

const IRON = rampFrom("#7d848c", "#2f3338")
const DARK_WOOD = rampFrom("#5e3b22")
const STAFF_WOOD = rampFrom("#9b6a3c")
const STAFF_GEM = rampFrom("#5fc4e8", "#1f4c66")
const RAPIER_GOLD = rampFrom("#d6b04a", "#5a4520")
const CROSSBOW_STOCK = rampFrom("#8a3e2c", "#3a1a12")

/** Any unit vector square to `d`, and a third square to both: a frame round a shaft. */
const around = (d: Vec3): [Vec3, Vec3] => {
    const a = normalize(Math.abs(d[1]) < 0.9 ? cross(d, [0, 1, 0]) : cross(d, [1, 0, 0]))
    return [a, normalize(cross(d, a))]
}

/** Spikes standing out of a ball round `at`, square to `d` and one off its end. */
const spikes = (at: Vec3, d: Vec3, r: number, length: number, m: Material): Primitive[] => {
    const [a, b] = around(d)
    const prims: Primitive[] = []
    for (let i = 0; i < 6; i++) {
        const t = (i / 6) * 2 * Math.PI
        const out = add(scale(a, Math.cos(t)), scale(b, Math.sin(t)))
        prims.push(segment(add(at, scale(out, r * 0.6)), add(at, scale(out, r + length)), Math.max(0.7, r * 0.3), m, "held"))
    }
    prims.push(segment(at, add(at, scale(d, r + length)), Math.max(0.7, r * 0.3), m, "held"))
    return prims
}

/** A haft through the fist along `d`: `behind` of its length below the fist, the rest ahead. */
const haft = (fist: Vec3, d: Vec3, length: number, behind: number, r: number, m: Material) => {
    const butt = add(fist, scale(d, -length * behind))
    const tip = add(fist, scale(d, length * (1 - behind)))
    return { butt, tip, prim: capsule(butt, tip, r, m, "held") }
}

/** A mace: a short haft and a flanged iron head, heavy enough to read as one. */
const mace = (j: LimbJoints, fist: Vec3, p: Proportions): Primitive[] => {
    const leg = legLength(p)
    const d = normalize(j.grip)
    const { tip, prim } = haft(fist, d, leg * 0.6, 0.15, Math.max(0.9, leg * 0.04), DARK_WOOD)
    const hr = Math.max(3, leg * 0.1)
    const head = add(tip, scale(d, -hr * 0.3))
    return [prim, ellipsoid(head, [1, 0, 0, 0, 1, 0, 0, 0, 1], [hr, hr, hr], IRON, "held"), ...spikes(head, d, hr, Math.max(1.4, leg * 0.045), IRON)]
}

/** A warhammer: a haft with a squared iron block across its end, a spike behind. */
const warhammer = (j: LimbJoints, fist: Vec3, p: Proportions): Primitive[] => {
    const leg = legLength(p)
    const d = normalize(j.grip)
    const { tip, prim } = haft(fist, d, leg * 0.78, 0.15, Math.max(0.8, leg * 0.04), DARK_WOOD)
    const { edge, basis } = bladeFrame(j, d)
    const hr = Math.max(2.6, leg * 0.09)
    const head = add(tip, scale(d, -hr * 0.5))
    return [
        prim,
        ellipsoid(add(head, scale(edge, hr * 0.5)), basis, [hr * 0.95, hr * 1.1, hr * 1.8], IRON, "held", { exponent: 4 }),
        segment(head, add(head, scale(edge, -hr * 2.4)), Math.max(0.7, hr * 0.35), IRON, "held"),
    ]
}

/** A flail: a short haft, a chain from its end that droops under the ball, a spiked iron ball. */
const flail = (j: LimbJoints, fist: Vec3, p: Proportions): Primitive[] => {
    const leg = legLength(p)
    const d = normalize(j.grip)
    const { tip, prim } = haft(fist, d, leg * 0.45, 0.2, Math.max(0.8, leg * 0.038), DARK_WOOD)
    const hr = Math.max(2.6, leg * 0.09)
    const chain = normalize(add(d, [0, -1.4, 0]))
    const ball = add(tip, scale(chain, leg * 0.3))
    const prims: Primitive[] = [prim]
    for (let i = 1; i <= 3; i++) prims.push(ellipsoid(lerp(tip, ball, i / 4), [1, 0, 0, 0, 1, 0, 0, 0, 1], [1, 1, 1], IRON, "held"))
    prims.push(ellipsoid(ball, [1, 0, 0, 0, 1, 0, 0, 0, 1], [hr, hr, hr], IRON, "held"), ...spikes(ball, chain, hr, Math.max(1.2, leg * 0.04), IRON))
    return prims
}

/** A war axe: a great axe for two hands, a long haft and a crescent blade either side of its head. */
const waraxe = (j: LimbJoints, fist: Vec3, p: Proportions): Primitive[] => {
    const leg = legLength(p)
    const d = normalize(j.grip)
    const len = leg * 1.15
    const r = Math.max(0.9, leg * 0.042)
    const { tip, prim } = haft(fist, d, len, 0.25, r, DARK_WOOD)
    const { edge, basis } = bladeFrame(j, d)
    const at = add(tip, scale(d, -len * 0.12))
    return [
        prim,
        ellipsoid(add(at, scale(edge, len * 0.12)), basis, [r * 0.9, len * 0.19, len * 0.12], AXE_HEAD, "held", { exponent: [2, 2.4, 2] }),
        ellipsoid(add(at, scale(edge, -len * 0.1)), basis, [r * 0.9, len * 0.15, len * 0.1], AXE_HEAD, "held", { exponent: [2, 2.4, 2] }),
    ]
}

/** A halberd: a pole taller than its bearer, an axe blade and a spike at the top, a hook behind. */
const halberd = (j: LimbJoints, fist: Vec3, p: Proportions): Primitive[] => {
    const leg = legLength(p)
    const d = normalize(j.grip)
    const len = leg * 1.85
    const r = Math.max(0.9, leg * 0.038)
    const { tip, prim } = haft(fist, d, len, 0.35, r, DARK_WOOD)
    const { edge, basis } = bladeFrame(j, d)
    const blade = add(add(tip, scale(d, -leg * 0.13)), scale(edge, leg * 0.13))
    return [
        prim,
        segment(tip, add(tip, scale(d, leg * 0.26)), Math.max(1.0, leg * 0.035), SWORD_STEEL, "held"),
        ellipsoid(blade, basis, [r * 0.9, leg * 0.2, leg * 0.14], SWORD_STEEL, "held", { exponent: [2, 2.6, 2] }),
        segment(add(tip, scale(d, -leg * 0.1)), add(add(tip, scale(d, -leg * 0.06)), scale(edge, -leg * 0.14)), Math.max(0.7, leg * 0.026), SWORD_STEEL, "held"),
    ]
}

/** A scythe: a long snath and, square to it at the top, a long blade curving down toward the hands. */
const scythe = (j: LimbJoints, fist: Vec3, p: Proportions): Primitive[] => {
    const leg = legLength(p)
    const d = normalize(j.grip)
    const len = leg * 1.7
    const { tip, prim } = haft(fist, d, len, 0.28, Math.max(0.9, leg * 0.036), DARK_WOOD)
    const { edge } = bladeFrame(j, d)
    // The blade runs out on the edge's side and bows back down the snath; its width lies along it.
    const thin = normalize(cross(d, edge))
    return [prim, ...blade(tip, edge, d, thin, leg * 0.75, Math.max(2.0, leg * 0.07), 0.5, Math.max(1.1, leg * 0.035), 0.3)]
}

/**
 * A rapier: a long, narrow, straight blade for the point, and a gold swept hilt -- a cup at the
 * guard, quillons across it and a knuckle-bow round the fingers to the pommel.
 */
const rapier = (j: LimbJoints, fist: Vec3, p: Proportions): Primitive[] => {
    const leg = legLength(p)
    const d = swordAxis(j)
    const { edge, thin } = bladeFrame(j, d)
    const guardAt = add(fist, scale(d, p.handRadius + 0.3))
    const capAt = add(fist, scale(d, -(p.handRadius + 0.4)))
    const prims = blade(guardAt, d, edge, thin, Math.max(22, leg * 0.95), Math.max(1.4, leg * 0.05), 0.4, Math.max(1.0, leg * 0.035))
    const r = Math.max(0.8, leg * 0.03)
    const bow = (u: number): Vec3 => add(add(guardAt, scale(d, -u * (p.handRadius * 2 + 0.7))),
        scale(edge, (p.handRadius + 0.7) * Math.sin(Math.PI * Math.min(1, u * 1.15)) + 0.6))
    for (let i = 0; i < 4; i++) prims.push(capsule(bow(i / 4), bow((i + 1) / 4), r, RAPIER_GOLD, "held"))
    prims.push(
        ellipsoid(guardAt, [1, 0, 0, 0, 1, 0, 0, 0, 1], [1.5, 1.5, 1.5], RAPIER_GOLD, "held"),
        capsule(add(guardAt, scale(edge, -2.2)), add(guardAt, scale(edge, 2.2)), Math.max(0.8, leg * 0.03), RAPIER_GOLD, "held"),
        ellipsoid(capAt, [1, 0, 0, 0, 1, 0, 0, 0, 1], [1.2, 1.2, 1.2], RAPIER_GOLD, "held"),
    )
    return prims
}

/** An iron pickaxe: a haft and, square across its end, a pick bar drawn to a point both ways. */
const pickaxe = (j: LimbJoints, fist: Vec3, p: Proportions): Primitive[] => {
    const leg = legLength(p)
    const d = normalize(j.grip)
    const { tip, prim } = haft(fist, d, leg * 0.85, 0.12, Math.max(0.8, leg * 0.04), AXE_HAFT)
    const { edge } = bladeFrame(j, d)
    const prims: Primitive[] = [prim]
    for (const s of [1, -1]) {
        // Each arm of the bar curves down toward the hand, thinning to its point.
        const mid = add(add(tip, scale(edge, s * leg * 0.12)), scale(d, -leg * 0.015))
        const end = add(add(tip, scale(edge, s * leg * 0.24)), scale(d, -leg * 0.07))
        prims.push(capsule(tip, mid, Math.max(1.1, leg * 0.04), IRON, "held"), capsule(mid, end, Math.max(0.7, leg * 0.026), IRON, "held"))
    }
    return prims
}

/** A spear: a long shaft and a leaf-shaped iron point on a socket. */
const spear = (j: LimbJoints, fist: Vec3, p: Proportions): Primitive[] => {
    const leg = legLength(p)
    const d = normalize(j.grip)
    const { tip, prim } = haft(fist, d, leg * 1.95, 0.35, Math.max(0.8, leg * 0.034), DARK_WOOD)
    const { basis } = bladeFrame(j, d)
    return [
        prim,
        ellipsoid(add(tip, scale(d, leg * 0.12)), basis, [Math.max(0.9, leg * 0.03), leg * 0.15, Math.max(1.8, leg * 0.065)], SWORD_STEEL, "held"),
        ellipsoid(tip, basis, [1.1, 1.0, 1.1], IRON, "held"),
    ]
}

/** A staff: a tall plain stave and, held in a knot of its wood at the top, a blue gem (LPC's). */
const staff = (j: LimbJoints, fist: Vec3, p: Proportions): Primitive[] => {
    const leg = legLength(p)
    const d = normalize(j.grip)
    const { tip, prim } = haft(fist, d, leg * 1.8, 0.3, Math.max(1.0, leg * 0.042), STAFF_WOOD)
    const gr = Math.max(1.6, leg * 0.06)
    const gem = add(tip, scale(d, gr * 0.9))
    const [a] = around(d)
    return [
        prim,
        ellipsoid(tip, [1, 0, 0, 0, 1, 0, 0, 0, 1], [gr * 0.9, gr * 0.9, gr * 0.9], STAFF_WOOD, "held"),
        segment(add(tip, scale(a, gr * 0.8)), add(gem, scale(a, gr * 0.9)), 0.6, STAFF_WOOD, "held"),
        segment(add(tip, scale(a, -gr * 0.8)), add(gem, scale(a, -gr * 0.9)), 0.6, STAFF_WOOD, "held"),
        ellipsoid(gem, [1, 0, 0, 0, 1, 0, 0, 0, 1], [gr, gr, gr], STAFF_GEM, "held"),
    ]
}

/**
 * A crossbow: a stock along the grip from behind the fist, a bow (the prod) across its front swept
 * back at the tips, the string drawn back to the nut and a bolt laid along the top.
 */
const crossbow = (j: LimbJoints, fist: Vec3, p: Proportions): Primitive[] => {
    const leg = legLength(p)
    const d = normalize(j.grip)
    const r = Math.max(1.0, leg * 0.045)
    const back = add(fist, scale(d, -leg * 0.14))
    const front = add(fist, scale(d, leg * 0.45))
    // The prod lies flat, across the stock: square to it and level, whatever the stock does.
    const level = cross(d, [0, 1, 0])
    const across = dot(level, level) > 1e-6 ? normalize(level) : around(d)[0]
    const up = normalize(cross(across, d))
    const nut = add(fist, scale(d, leg * 0.16))
    const prims: Primitive[] = [capsule(back, front, r, CROSSBOW_STOCK, "held")]
    for (const s of [1, -1]) {
        const tipAt = add(add(front, scale(across, s * leg * 0.3)), scale(d, -leg * 0.08))
        prims.push(capsule(front, tipAt, Math.max(0.8, leg * 0.03), BOW_GRIP, "held"))
        prims.push(capsule(tipAt, nut, 0.45, BOW_STRING, "held"))
    }
    prims.push(capsule(add(nut, scale(up, r * 0.8)), add(front, add(scale(d, leg * 0.06), scale(up, r * 0.8))), 0.5, ARROW_SHAFT, "held"))
    return prims
}

/**
 * Pantaloons: loose, full trousers. Roomy through the seat and thigh, blousing over the knee and
 * calf, then gathered into a band three-quarters of the way down the shin, the ankle left bare --
 * LPC's pantaloons, on the Ember body's own volumes. `t` is the body's cloth thickness.
 */
const pantaloonsOf = (sk: Skeleton, p: Proportions, m: Material, t: number): Primitive[] => {
    const prims = lowerBody(sk, p, m, 0.8 * t, undefined, true)
    // How far the cloth stands off the leg: more than trousers' half-pixel, on any body.
    const loose = 0.8 + 0.7 * t
    for (const side of SIDES) {
        const j = sk[side]
        const band = lerp(j.knee, j.ankle, 0.74)
        // The fullness hangs off the OUTSIDE of the leg, as the cut drapes: the legs stay apart
        // below the seat instead of filling the gap between them into one sack.
        const out = scale(apply(sk.pelvisBasis, [sideSign(side), 0, 0]), loose * 0.45)
        prims.push(segment(add(j.hip, scale(out, 0.5)), add(j.knee, out), p.thighRadius + loose, m, legOf(side)))
        prims.push(joint(add(j.knee, out), p.shinRadius * 0.9 + loose * 1.1, m, legOf(side)))
        // The blouse: fullest just below the knee, drawn in toward the band.
        prims.push(segment(add(j.knee, out), add(band, scale(out, 0.6)), p.shinRadius + loose * 1.2, m, legOf(side)))
        // The band the fullness is gathered into, snug on the calf.
        prims.push(as(capsule(lerp(j.knee, j.ankle, 0.7), lerp(j.knee, j.ankle, 0.8), p.shinRadius * 0.8 + 0.35 * t, m, legOf(side)), "band"))
    }
    return prims
}

/**
 * Skirt-like garments: one volume round both legs from the hips down to `hem`, not a tube per leg.
 * `hem` is how far down the leg, in segments: 1 the knee, 1.7 the lower calf, 0.55 mid-thigh. It is
 * a stack of deep, overlapping slices, each spanning the two legs at the same distance down them
 * and standing further off toward the hem, so it flares a little, swings with the stride, and the
 * legs step out beneath. A lining down each leg (`lining`: its material, how far down the leg it
 * starts as a share of the length, how far it stands off) keeps a knee from showing through.
 * `slice` adds to each slice: a pattern painted on, or cut out of it (`toWorld` places a point of
 * the slice's own frame on the body).
 */
const skirtLike = (sk: Skeleton, p: Proportions, m: Material, t: number, hem: number,
    lining: { m: Material; from?: number; off?: number },
    slice: (i: number, n: number, radii: Vec3, toWorld: (q: Vec3) => Vec3) => Partial<Ellipsoid> = () => ({})): Primitive[] => {
    const prims: Primitive[] = lowerBody(sk, p, m, 0.9 * t, undefined, true).map(prim => ({ ...prim, group: "skirt" as PartGroup }))
    const off = lining.off ?? 0.5 + 0.3 * t
    // Each leg as hip -> knee -> hem, walked by distance.
    const path = (side: Side) => {
        const j = sk[side]
        const end = hem <= 1 ? lerp(j.hip, j.knee, hem) : lerp(j.knee, j.ankle, hem - 1)
        if (hem <= 1) {
            prims.push(capsule(lerp(j.hip, end, lining.from ?? 0), end, p.thighRadius + off, lining.m, "skirt"))
        } else {
            prims.push(segment(j.hip, j.knee, p.thighRadius + off, lining.m, "skirt"))
            prims.push(joint(j.knee, p.shinRadius * 0.9 + off, lining.m, "skirt"))
            prims.push(segment(j.knee, end, p.shinRadius + off, lining.m, "skirt"))
        }
        const knee = hem <= 1 ? end : j.knee
        const upper = Math.sqrt(dot(sub(knee, j.hip), sub(knee, j.hip)))
        const lower = hem <= 1 ? 0 : Math.sqrt(dot(sub(end, j.knee), sub(end, j.knee)))
        return {
            length: upper + lower,
            at: (f: number): { at: Vec3; along: Vec3 } => {
                const d = f * (upper + lower)
                return d <= upper
                    ? { at: lerp(j.hip, knee, d / upper), along: normalize(sub(knee, j.hip)) }
                    : { at: lerp(j.knee, end, (d - upper) / lower), along: normalize(sub(end, j.knee)) }
            },
        }
    }
    const legL = path("L"), legR = path("R")
    const N = Math.max(4, Math.round(hem * 6))
    const spacing = (legL.length + legR.length) / 2 / N
    for (let i = 1; i <= N; i++) {
        const f = i / N
        const a = legL.at(f), b = legR.at(f)
        const center = scale(add(a.at, b.at), 0.5)
        // Slices hang along the legs' mean line, spanning from one leg to the other.
        const y = normalize(scale(add(a.along, b.along), -1))
        const span = sub(a.at, b.at)
        const flat = sub(span, scale(y, dot(span, y)))
        const x = dot(flat, flat) > 1e-6 ? normalize(flat) : normalize(apply(sk.pelvisBasis, [1, 0, 0]))
        const z = normalize(cross(x, y))
        const basis: Mat3 = [x[0], y[0], z[0], x[1], y[1], z[1], x[2], y[2], z[2]]
        // A leg slanting through a slice is wider across it than it is thick.
        const slant = Math.max(0.6, Math.min(-dot(a.along, y), -dot(b.along, y)))
        const legRadius = hem <= 1 || f < 0.5 ? p.thighRadius : p.shinRadius
        const reach = (legRadius + 0.5 + 0.6 * t + (hem / 1.7) * f) / slant
        const half = Math.sqrt(dot(flat, flat)) / 2
        const radii: Vec3 = [half + reach, spacing * 1.2, reach]
        // Squared off across and front to back (an ellipse spanning both legs is too thin where each
        // leg stands in it) and drum-like top to bottom, overlapping its neighbours, so the stack
        // reads as one smooth fall of cloth rather than tiers.
        const toWorld = (q: Vec3): Vec3 => add(center, apply(basis, q))
        prims.push(ellipsoid(center, basis, radii, m, "skirt", { exponent: [3.5, 4, 3.5], ...slice(i, N, radii, toWorld) }))
    }
    return prims
}

/** A skirt to the lower calf, round both legs, the feet stepping out beneath it. */
const skirtOf = (sk: Skeleton, p: Proportions, m: Material, t: number): Primitive[] => skirtLike(sk, p, m, t, 1.7, { m })

const LEGION_LEATHER = rampFrom("#5e3017")
const LEGION_TUNIC = rampFrom("#8e2a22")
const LEGION_STRAPS = 12

/**
 * A legionary's pteruges: a short skirt of leather straps hanging from a studded belt to mid-thigh,
 * each strap tipped with a brass stud and hanging free at the bottom, where the red tunic worn
 * under it shows between them. The item comes in one colour.
 */
const legionOf = (sk: Skeleton, p: Proportions, t: number): Primitive[] => {
    // Which strap a point is on, and how far across it (0..1). Straps run round the body's own
    // upright axis, the same for every slice, so a seam falls straight down the skirt.
    const ax = apply(sk.pelvisBasis, [1, 0, 0]), az = apply(sk.pelvisBasis, [0, 0, 1])
    const strap = (world: Vec3, count = LEGION_STRAPS): number => {
        const v = sub(world, sk.pelvis)
        return ((Math.atan2(dot(v, az), dot(v, ax)) / (2 * Math.PI) + 0.5) * count) % 1
    }
    // The tunic only where the straps hang free: higher up it would show past the leather.
    const prims = skirtLike(sk, p, LEGION_LEATHER, t, 0.55, { m: LEGION_TUNIC, from: 0.5, off: 0.3 }, (i, n, radii, toWorld) => {
        const free = i > n * 0.55
        return {
            // The seams between straps; a brass stud on each strap just above its foot.
            decal: q => {
                const u = strap(toWorld(q))
                // A seam a pixel wide on a strap about three across.
                if (u < 0.3) return LEGION_LEATHER.line
                if (i === n && u > 0.5 && u < 0.85 && Math.abs(q[1]) < radii[1] * 0.45) return SWORD_BRASS.base
                return undefined
            },
            // Below the belt's reach the straps hang free: cut apart, the tunic between them.
            keep: free ? (q => strap(toWorld(q)) >= 0.34) : undefined,
        }
    })
    // Above the tunic, a leather lining: a thigh swung back in a run would otherwise show at the seat.
    for (const side of SIDES) {
        const j = sk[side]
        // Short and close: its rounded end must stop above the gaps, where the tunic shows.
        prims.push(capsule(j.hip, lerp(j.hip, j.knee, 0.15), p.thighRadius + 0.2, LEGION_LEATHER, "skirt"))
    }
    // The belt: brass plates all round with dark joins, standing proud of the straps' tops.
    const waist = add(sk.pelvis, apply(sk.pelvisBasis, [0, p.pelvis[1] * 0.55, 0]))
    const radii: Vec3 = [p.pelvis[0] + 1.6 * t, 1.1, p.pelvis[2] + 1.6 * t]
    prims.push(ellipsoid(waist, sk.pelvisBasis, radii, LEGION_LEATHER, "skirt", {
        exponent: [3, 4, 3],
        decal: q => (strap(add(waist, apply(sk.pelvisBasis, q)), 12) < 0.72 ? SWORD_BRASS.base : LEGION_LEATHER.deep),
    }))
    return prims
}

/** Body armour Ember draws: a leather jerkin, a legionary's banded cuirass, a plate breastplate. */
export type ArmorKind = "leather" | "legion" | "plate"

/** Each kind's colours, by the name the item gives them. */
const ARMOR_COLORS: Record<ArmorKind, Record<string, string>> = {
    leather: { brown: "#6b4a30", blue: "#3d5170", green: "#4c5f36" },
    // Metal sits mid-tone: its glint (metalFrom) carries the shine. Pale steel reads as a white shirt.
    legion: { brown: "#a5712f", gray: "#87919b", gold: "#c39a34" },
    plate: { gray: "#6b727a", metal: "#88929c", yellow: "#c2a240", gold: "#c39a34", orange: "#b0662f", tan: "#9e8866" },
}
const ARMOR_BUCKLE = metalFrom("#c3c8cc")

/**
 * Paint ellipsoids by where a point of them is on the body and which way the surface faces there
 * (their decals get local points; the normal is the superellipsoid's gradient, as the raster's).
 */
const painted = (prims: Primitive[], paint: (world: Vec3, normal: Vec3) => number | undefined): Primitive[] =>
    prims.map(prim => {
        if (prim.kind !== "ellipsoid") return prim
        const e = typeof prim.exponent === "number" ? [prim.exponent, prim.exponent, prim.exponent] : prim.exponent ?? [2, 2, 2]
        return {
            ...prim,
            decal: (q: Vec3) => {
                const g = q.map((v, i) => (Math.sign(v) * Math.pow(Math.abs(v) / prim.radii[i], e[i] - 1)) / prim.radii[i]) as Vec3
                return paint(add(prim.center, apply(prim.basis, q)), normalize(apply(prim.basis, g)))
            },
        }
    })

/**
 * Metal's own light, for a body turned to `facing`. Polished metal shows what it reflects, not how
 * much light falls on it: where the reflection of the camera's view looks up at the sky it is lit,
 * brightest just above the horizon, then a hard dark band where it starts to show the ground,
 * easing a little lighter below; plus a tight glint toward the lamp. The broad lit side cloth gets
 * reads, on a cuirass, as a t-shirt.
 */
const sheen = (m: Material, facing: Facing) => {
    const turn = rotY(FACING_YAW[facing])
    const light = DEFAULT_RASTER.light
    const view: Vec3 = [0, Math.sin(DEFAULT_RASTER.pitch), Math.cos(DEFAULT_RASTER.pitch)]
    const half = normalize(add(light, view))
    return (normal: Vec3): number => {
        const n = apply(turn, normal)
        if (dot(n, half) > 0.95) return m.light
        // The view reflected off the surface: how far up (sky) or down (ground) it looks.
        const skyward = 2 * dot(n, view) * n[1] - view[1]
        if (dot(n, light) < -0.35) return m.deep
        return skyward > 0.45 ? m.base : skyward > 0.08 ? m.light : skyward > -0.22 ? m.deep : m.shade
    }
}

/**
 * Body armour, a hard shell over the chest and hips standing off any shirt, sleeveless as LPC draws
 * it (sleeves show beneath). Leather: a jerkin to the hips, belted, a metal buckle at the front.
 * Legion: overlapping bands round the torso to the waist, following its lean. Plate: one smooth
 * breastplate to the top of the hips, a raised ridge down its middle. Metal takes metalFrom's
 * hard glint; leather the softer cloth ramp.
 */
const armorOf = (sk: Skeleton, p: Proportions, kind: ArmorKind, color: string, t: number, facing: Facing): Primitive[] => {
    const tone = ARMOR_COLORS[kind][color] ?? Object.values(ARMOR_COLORS[kind])[0]
    const m = kind === "leather" ? rampFrom(tone) : metalFrom(tone)
    // Stands off the body further than a shirt does (0.6), and further still for metal.
    const by = kind === "leather" ? 0.9 * t + 0.1 : 1.0 * t + 0.35
    const chest = upperBody(sk, p, m, by, facing).filter(prim => prim.group === "torso" || prim.group === "bust")
    // How far down the hips it reaches (a share of the pelvis's height below its middle).
    const reach = kind === "leather" ? 0.5 : kind === "legion" ? 0.05 : 0.25
    const hips = lowerBody(sk, p, m, by + 0.15, q => q[1] > -p.pelvis[1] * reach)
    const up = apply(sk.torsoBasis, [0, 1, 0]), across = apply(sk.torsoBasis, [1, 0, 0]), ahead = apply(sk.torsoBasis, [0, 0, 1])
    if (kind === "legion") {
        // Bands of plate a few pixels deep, a dark seam a pixel wide where each overlaps the next.
        const band = 2.6
        const shine = sheen(m, facing)
        return painted([...chest, ...hips], (w, n) => {
            const y = dot(sub(w, sk.chest), up) / band
            return y - Math.floor(y) < 0.38 ? m.deep : shine(n)
        })
    }
    if (kind === "plate") {
        // A rigid shell, not the body's own shape: the breastplate swells out ahead of the chest, a
        // barrel in profile, and ends in a flared fauld over the hips -- a hard edge cloth never has.
        const breast = add(sk.chest, apply(sk.torsoBasis, [0, -0.4, p.posture + 0.7]))
        const shell = ellipsoid(breast, sk.torsoBasis, [p.torso[0] + by * 0.6, p.torso[1] * 0.9 + by, p.torso[2] + by + 0.5], m, "torso", {
            exponent: [3, 2.4, 2],
        })
        const fauldAt = add(sk.pelvis, apply(sk.pelvisBasis, [0, p.pelvis[1] * 0.15, 0.2]))
        const fauld = ellipsoid(fauldAt, sk.pelvisBasis, [p.pelvis[0] + by + 0.8, 1.4, p.pelvis[2] + by + 0.9], m, "torso", {
            exponent: [2.6, 4, 2.6],
        })
        const shine = sheen(m, facing)
        // The ridge down the front's middle: its lit edge toward the light (the body's right, -x)
        // and its shadowed flank beside it, as a raised crease catches the light.
        return painted([...chest, shell, ...hips, fauld], (w, n) => {
            const v = sub(w, sk.chest)
            const x = dot(v, across)
            if (dot(v, ahead) > 0 && dot(v, up) > -p.spine) {
                if (x > -0.6 && x <= 0.3) return m.light
                if (x > 0.3 && x < 1.1) return m.deep
            }
            return shine(n)
        })
    }
    // Leather: belted at the waist, the buckle in front.
    const waist = add(sk.pelvis, apply(sk.pelvisBasis, [0, p.pelvis[1] * 0.45, 0]))
    const belt: Vec3 = [p.pelvis[0] + by + 0.9, 1.0, p.pelvis[2] + by + 0.9]
    return [
        ...chest,
        ...hips,
        ellipsoid(waist, sk.pelvisBasis, belt, rampFrom("#3a2a20"), "torso", {
            exponent: [3, 4, 3],
            // The pelvis frame's +z is the front.
            decal: q => (Math.abs(q[0]) < 1.0 && q[2] > 0 ? ARMOR_BUCKLE.light : undefined),
        }),
    ]
}

/**
 * Armoured leg wear (the game's pants-armor), LPC's steel leggings: plate over the seat, cuisses down
 * each thigh in overlapping lames, a cop standing proud over the knee, a greave down the shin to the
 * ankle -- in the plate armour's metals. Shaded as legs are (mostly shade and deep: LPC's greaves are
 * dark steel); metal's reflection bands (sheen) striped a round leg like camouflage. The knee cop
 * alone takes the glint. Each plate is a limb segment of its own, so the raster lines it where it
 * overlaps the next, as articulated plate reads.
 */
const greavesOf = (sk: Skeleton, p: Proportions, color: string, t: number, facing: Facing): Primitive[] => {
    const m = metalFrom(ARMOR_COLORS.plate[color] ?? ARMOR_COLORS.plate.metal)
    const lit = lighting(facing)
    // Stands off the leg a little further than trousers (0.45): plate over padding.
    const by = 0.6 * t + 0.25
    const prims: Primitive[] = lowerBody(sk, p, m, by + 0.1, undefined, true)
    for (const side of SIDES) {
        const j = sk[side]
        const g = legOf(side)
        const along = normalize(sub(j.knee, j.hip))
        // Three lames down the thigh, a dark seam a pixel wide where each overlaps the next.
        const lame = distance(j.hip, j.knee) / 3
        prims.push(...painted(thighOf(sk, side, p, by, m, g).map(prim => as(prim, "thigh")), w => {
            const d = dot(sub(w, j.hip), along) / lame
            return d > 0.8 && d < 2.8 && d - Math.floor(d) < 0.3 ? m.deep : undefined
        }))
        prims.push(...painted([as(joint(j.knee, p.shinRadius * 0.9 + by + 0.6, m, g), "knee")], (_w, n) => (lit(n) === 0 ? m.light : undefined)))
        prims.push(...shinOf(sk, side, p, by, m, g, 0.94).map(prim => as(prim, "shin")))
    }
    return prims
}

/** The bauldron's leather, by the colour the item names (it comes in brown); LPC's dark red-brown. */
const BAULDRON_COLORS: Record<string, string> = { brown: "#5a2410" }
const BAULDRON_TIP = metalFrom("#9a9a9a")

/**
 * The game's bauldron, as LPC draws it: a leather pauldron capping the RIGHT shoulder, two lames
 * deep, hard-edged and standing off whatever the shoulder wears (`under`), and the strap that holds
 * it on (bauldronStrap). Side-on with that shoulder away it is hidden, as the far deltoid is.
 */
const bauldronOf = (sk: Skeleton, p: Proportions, m: Material, under: number, facing: Facing): Primitive[] => {
    if ((facing === "left" || facing === "right") && farSide(facing) === "R") return []
    const j = sk.R
    const out = apply(sk.torsoBasis, [sideSign("R"), 0, 0])
    const lit = lighting(facing)
    // The dome over the deltoid, open underneath so the arm hangs out of it.
    const r = p.deltoidRadius + under + 0.9
    const cap = add(j.shoulder, apply(sk.torsoBasis, [sideSign("R") * 0.6, 0.7, 0]))
    const dome = ellipsoid(cap, sk.torsoBasis, [r + 0.2, r * 0.8, r + 0.2], m, "armR", { part: "pauldron", keep: q => q[1] > -r * 0.35 })
    // The lame below it, round the top of the upper arm and turned with it, its outer half only.
    const arm = distance(j.shoulder, j.elbow)
    const lame = as(segment(j.shoulder, lerp(j.shoulder, j.elbow, 0.45), p.upperArmRadius + under + 0.7, m, "armR"), "pauldron")
    const lower: Ellipsoid = { ...lame, keep: q => dot(apply(lame.basis, q), out) > -0.3 && q[1] < arm * 0.12 }
    // Its rim a lit edge; the seam where the dome overlaps the lame, dark.
    return painted([dome, lower], (w, n) => {
        const below = dot(sub(w, cap), apply(sk.torsoBasis, [0, 1, 0]))
        if (Math.abs(below + r * 0.3) < 0.45) return m.deep
        return tone(m, lit(n))
    })
}

/**
 * The bauldron's strap, painted on every torso surface -- skin, shirt or armour, whichever the camera
 * sees -- rather than built as a shell standing off the outermost: a band in the chest's frame from
 * the top of the right shoulder down across the chest to the left side and the same way round the
 * back, as a baldric runs. The band ignores depth, so front and back are one loop. Its steel tip
 * hangs at the left side in front, as LPC's does.
 */
const bauldronStrap = (sk: Skeleton, p: Proportions, m: Material, facing: Facing) => {
    const lit = lighting(facing)
    const across = apply(sk.torsoBasis, [1, 0, 0]), up = apply(sk.torsoBasis, [0, 1, 0]), ahead = apply(sk.torsoBasis, [0, 0, 1])
    // From the right shoulder's top (its x and height in the chest's frame) to the left side, low.
    const shoulder = sub(sk.R.shoulder, sk.chest)
    const from: [number, number] = [dot(shoulder, across) * 0.7, dot(shoulder, up) + p.deltoidRadius * 0.6]
    const to: [number, number] = [p.torso[0] * 0.95, -p.torso[1] * 0.55]
    const run: [number, number] = [to[0] - from[0], to[1] - from[1]]
    const length = Math.hypot(run[0], run[1])
    const dir: [number, number] = [run[0] / length, run[1] / length]
    // A strap two pixels wide on any body.
    const half = Math.max(1.05, p.torso[0] * 0.13)
    return (w: Vec3, n: Vec3): number | undefined => {
        const v = sub(w, sk.chest)
        const x = dot(v, across) - from[0], y = dot(v, up) - from[1]
        const s = x * dir[0] + y * dir[1]
        if (s < -half * 2 || s > length) return undefined
        // Across the strap: + above its middle line, - below.
        const d = x * -dir[1] + y * dir[0]
        if (Math.abs(d) > half) return undefined
        if (s > length - half * 1.6 && dot(v, ahead) > 0) return tone(BAULDRON_TIP, lit(n))
        // Its lower edge in shadow, as a strap lying on the chest throws one.
        return d < -half * 0.35 ? m.deep : tone(m, Math.min(2, lit(n)))
    }
}

/** Paint over prims' own decals (theirs still shows wherever `paint` leaves a point alone). */
const overPainted = (prims: Primitive[], paint: (world: Vec3, normal: Vec3) => number | undefined): Primitive[] =>
    prims.map(prim => {
        if (prim.kind !== "ellipsoid") return prim
        const own = prim.decal
        const [over] = painted([prim], paint) as Ellipsoid[]
        return { ...over, decal: (q: Vec3) => over.decal!(q) ?? own?.(q) }
    })

/** Painted light for a body turned to `facing`: a ramp step, 0 lit to 3 deep (decals skip the raster's shading). */
const lighting = (facing: Facing) => {
    const turn = rotY(FACING_YAW[facing])
    const light = DEFAULT_RASTER.light
    return (normal: Vec3): number => {
        const lit = dot(apply(turn, normal), light)
        return lit > 0.6 ? 0 : lit > 0.05 ? 1 : lit > -0.45 ? 2 : 3
    }
}
const tone = (m: Material, step: number): number => [m.light, m.base, m.shade, m.deep][step]

type ShieldShape = "heater" | "round" | "aspis"
interface ShieldDesign {
    shape: ShieldShape
    field: Material
    rim: Material
    /** A device painted on the field: a cross, a chevron, the Spartans' lambda. */
    charge?: { kind: "cross" | "chevron" | "lambda"; m: Material }
    /** A polished metal face (metal's light, `sheen`), not paint. */
    metal?: boolean
}
const TINCTURES: Record<string, string> = {
    white: "#dcd6c8", gray: "#8b9096", blue: "#3e63a0", green: "#4f7a3a", red: "#a33a33", orange: "#c06a2c",
}
const tincture = (name: string) => rampFrom(TINCTURES[name] ?? TINCTURES.gray)
const SHIELD_STEEL = metalFrom("#9aa2aa")
const SHIELD_IRON = metalFrom("#5d6268")
const SHIELD_BRASS = metalFrom("#b8924a")
const SHIELD_WOOD = rampFrom("#7a5134")
const SHIELD_BACK = rampFrom("#5e4029")
const SHIELD_BRONZE = metalFrom("#a5712f")

/**
 * What each of the game's shields looks like. Kite shields name their field in the type and their
 * charge in the colour (`shield-kite_red` in gray: a gray chevron on red), as LPC's sheets do.
 */
const shieldDesign = (type: string, color: string): ShieldDesign | undefined => {
    if (type === "shield-crusader") return { shape: "heater", field: tincture("white"), rim: SHIELD_STEEL, charge: { kind: "cross", m: rampFrom("#9b2a24") } }
    if (type === "shield-kite") return { shape: "heater", field: tincture(color), rim: SHIELD_STEEL }
    const kite = /^shield-kite_(\w+)$/.exec(type)
    if (kite) return { shape: "heater", field: tincture(kite[1]), rim: SHIELD_BRASS, charge: { kind: "chevron", m: tincture(color) } }
    if (type === "shield-round") return { shape: "round", field: SHIELD_WOOD, rim: SHIELD_IRON }
    if (type === "shield-spartan") return { shape: "aspis", field: SHIELD_BRONZE, rim: SHIELD_BRONZE, metal: true, charge: { kind: "lambda", m: metalFrom("#6e4a1f") } }
    return undefined
}

/** Which way a shield's face is turned from the body's forward, per facing: toward the camera, as LPC draws it. */
const SHIELD_TURN: Record<Facing, number> = {
    front: deg(15), back: deg(25), left: deg(55), right: deg(-55),
    // Halfway between the neighbouring cardinal grips.
    frontleft: deg(35), frontright: deg(-20), backleft: deg(40), backright: deg(-15),
}

/**
 * A shield gripped in the left fist (centre grip, as round shields are held; LPC holds every shield
 * so), its face turned toward the camera for the facing as LPC draws it, or slung on the back while
 * the hand has a bow. A heater is a flat slab cut to a point below; a round shield a disc with an
 * iron rim and boss over planks; the Spartan aspis a bronze dome with a raised rim and its lambda.
 * Paint is lit here (decals skip the raster's shading); the back shows bare wood.
 */
const shieldOf = (sk: Skeleton, p: Proportions, look: NonNullable<EmberLook["shield"]>, facing: Facing): Primitive[] => {
    const design = shieldDesign(look.type, look.color)
    if (!design) return []
    const leg = legLength(p)
    const up = apply(sk.torsoBasis, [0, 1, 0])
    // Sized to the body, never so small it reads as a buckler on a short-legged LPC body.
    const w = design.shape === "heater" ? Math.max(6.5, leg * 0.3) : Math.max(8, leg * (design.shape === "aspis" ? 0.42 : 0.38))
    const h = design.shape === "heater" ? Math.max(9, leg * 0.42) : w
    const thick = design.shape === "aspis" ? 2.8 : 0.9
    let face: Vec3, center: Vec3
    if (look.slung) {
        face = apply(sk.torsoBasis, [0.25, 0, -1])
        center = add(sk.chest, apply(sk.torsoBasis, [0.5, -1.5, -(p.torso[2] + thick + 1.2)]))
    } else {
        const turn = SHIELD_TURN[facing]
        face = apply(sk.torsoBasis, [Math.sin(turn), 0, Math.cos(turn)])
        center = add(fistCenter(sk, p, "L"), scale(face, p.handRadius * 0.7 + thick))
    }
    const n = normalize(face)
    const x = normalize(cross(up, n))
    const y = normalize(cross(n, x))
    const basis: Mat3 = [x[0], y[0], n[0], x[1], y[1], n[1], x[2], y[2], n[2]]
    const toLocal = (world: Vec3) => apply(transpose(basis), sub(world, center))
    // A heater's sides run straight, then curve in to the point.
    const halfWidth = (yn: number) => (design.shape !== "heater" ? Math.sqrt(Math.max(0, 1 - yn * yn)) : yn > -0.2 ? 1 : Math.sqrt(Math.max(0, 1 - ((-0.2 - yn) / 0.8) ** 2)))
    const charged = (xn: number, yn: number): boolean => {
        const c = design.charge
        if (!c) return false
        if (c.kind === "cross") return (Math.abs(xn) < 0.14 && yn > -0.7 && yn < 0.8) || (Math.abs(yn - 0.3) < 0.11 && Math.abs(xn) < 0.62)
        if (c.kind === "chevron") return Math.abs(yn - (0.35 - 0.85 * Math.abs(xn))) < 0.14
        // The lambda: two strokes down from an apex.
        const stroke = (sx: number) => {
            const ax = 0, ay = 0.5, bx = sx * 0.42, by = -0.5
            const t = Math.max(0, Math.min(1, ((xn - ax) * (bx - ax) + (yn - ay) * (by - ay)) / ((bx - ax) ** 2 + (by - ay) ** 2)))
            return Math.hypot(xn - (ax + t * (bx - ax)), yn - (ay + t * (by - ay))) < 0.09
        }
        return stroke(1) || stroke(-1)
    }
    const lit = lighting(facing)
    const shine = sheen(design.field, facing)
    const rimWidth = design.shape === "aspis" ? 1.5 : 1.1
    const paint = (world: Vec3, normal: Vec3): number => {
        const [lx, ly, lz] = toLocal(world)
        const step = lit(normal)
        if (lz < 0) return tone(SHIELD_BACK, step)
        const xn = lx / w, yn = ly / h
        const edge = Math.min(w * halfWidth(yn) - Math.abs(lx), design.shape === "heater" ? h - ly : w * 9)
        if (edge < rimWidth || (design.shape !== "heater" && Math.hypot(lx, ly) > w - rimWidth)) return tone(design.rim, step)
        if (charged(xn, yn)) return tone(design.charge!.m, step)
        if (design.metal) return shine(normal)
        // Planks on a round shield, a seam every few pixels.
        if (design.shape === "round" && ((lx / 2.6) % 1 + 1) % 1 < 0.2) return tone(design.field, Math.min(3, step + 1))
        return tone(design.field, step)
    }
    const slab = ellipsoid(center, basis, [w, h, thick], design.field, "held", {
        exponent: design.shape === "heater" ? [6, 6, 2] : design.shape === "round" ? [2, 2, 4] : [2, 2, 2],
        keep: design.shape === "heater" ? q => Math.abs(q[0]) <= w * halfWidth(q[1] / h) : undefined,
    })
    const prims = painted([slab], paint)
    if (design.shape === "round" && !look.slung) {
        // The iron boss over the grip.
        const boss = ellipsoid(add(center, scale(n, thick * 0.8)), basis, [w * 0.24, w * 0.24, 1.5], SHIELD_IRON, "held")
        const shineBoss = sheen(SHIELD_IRON, facing)
        prims.push(...painted([boss], (_w, normal) => shineBoss(normal)))
    }
    return prims
}

/**
 * A head of hair as a few numbers, so every one of the game's styles is a record, not a drawing:
 * how far it stands off the skull, how low the fringe comes, how long it falls, and what it has
 * besides -- sides shaved, something on top, a tie, a texture.
 */
export interface HairDesign {
    /** Stand-off of the cap over the skull: 0.3 a buzz cut, 0.9 a short cut, 3 an afro. */
    volume: number
    /** The front hairline: 0 high off the brow, 0.35 a short cut's, 1 bangs to the eyebrows. */
    fringe: number
    /** Down the back: to the nape, framing the face to the jaw, or falling to the shoulders, mid-back, waist. */
    length: "nape" | "jaw" | "shoulder" | "back" | "waist"
    /** Sides: shaved to the skin (a crest left on top), or faded close. The crown: bald. */
    sides?: "shaved" | "faded"
    crown?: "bald"
    top?: "spikes" | "tufts" | "tuft" | "tall-tuft" | "crest" | "tall-crest" | "flat" | "quiff" | "beehive"
    tie?: "ponytail" | "high-ponytail" | "bun" | "topknot" | "bunches" | "braid"
    texture?: "curls" | "locks" | "rows"
}

const D = (volume: number, fringe: number, length: HairDesign["length"], more: Partial<HairDesign> = {}): HairDesign =>
    ({ volume, fringe, length, ...more })

/**
 * Ember's own styles (crop, short, long, ponytail -- drawn exactly as before they were records) and
 * each of the game's LPC styles as the nearest design (`long` and `ponytail` are both).
 */
export const HAIR_DESIGNS: Record<string, HairDesign> = {
    crop: D(0.5, 0, "nape"),
    short: D(0.9, 0.35, "nape"),
    long: D(0.9, 0.35, "shoulder"),
    ponytail: D(0.9, 0.35, "nape", { tie: "ponytail" }),
    afro: D(3, 0.3, "nape", { texture: "curls" }),
    balding: D(0.6, 0, "nape", { crown: "bald" }),
    bangs: D(1, 1, "nape"),
    bangs_bun: D(1, 1, "nape", { tie: "bun" }),
    bangslong: D(1, 1, "back"),
    bangslong2: D(1, 1, "waist"),
    bangsshort: D(0.8, 0.9, "nape"),
    bedhead: D(1.2, 0.5, "nape", { top: "tufts" }),
    bob: D(1, 0.7, "jaw"),
    bob_side_part: D(1, 0.5, "jaw"),
    braid: D(0.9, 0.35, "nape", { tie: "braid" }),
    braid2: D(0.9, 0.35, "nape", { tie: "braid" }),
    bunches: D(0.9, 0.6, "nape", { tie: "bunches" }),
    buzzcut: D(0.3, 0.1, "nape"),
    cornrows: D(0.4, 0.2, "nape", { texture: "rows" }),
    cowlick: D(0.9, 0.35, "nape", { top: "tuft" }),
    cowlick_tall: D(0.9, 0.35, "nape", { top: "tall-tuft" }),
    curly_long: D(1.4, 0.4, "back", { texture: "curls" }),
    curly_short: D(1.4, 0.4, "nape", { texture: "curls" }),
    curtains: D(1, 0.6, "jaw"),
    curtains_long: D(1, 0.6, "shoulder"),
    dreadlocks_long: D(1.2, 0.5, "back", { texture: "locks" }),
    dreadlocks_short: D(1.2, 0.5, "jaw", { texture: "locks" }),
    flat_top_fade: D(0.9, 0.2, "nape", { sides: "faded", top: "flat" }),
    flat_top_straight: D(0.9, 0.2, "nape", { top: "flat" }),
    half_up: D(1, 0.4, "shoulder", { tie: "bun" }),
    halfmessy: D(1.1, 0.5, "jaw", { top: "tufts" }),
    high_and_tight: D(0.7, 0.15, "nape", { sides: "faded" }),
    high_ponytail: D(0.9, 0.3, "nape", { tie: "high-ponytail" }),
    idol: D(0.9, 0.2, "nape", { top: "quiff" }),
    jewfro: D(1.9, 0.4, "nape", { texture: "curls" }),
    lob: D(1, 0.5, "shoulder"),
    long_band: D(1, 0.3, "back"),
    long_center_part: D(1, 0.4, "back"),
    long_messy: D(1.2, 0.5, "back", { top: "tufts" }),
    long_messy2: D(1.2, 0.6, "back", { top: "tufts" }),
    long_tied: D(0.9, 0.35, "nape", { tie: "ponytail" }),
    longhawk: D(0.4, 0.1, "nape", { sides: "shaved", top: "tall-crest" }),
    longknot: D(1, 0.35, "shoulder", { tie: "topknot" }),
    loose: D(1.1, 0.4, "back"),
    messy1: D(1.1, 0.5, "nape", { top: "tufts" }),
    messy2: D(1.1, 0.6, "nape", { top: "tufts" }),
    messy3: D(1.2, 0.5, "jaw", { top: "tufts" }),
    mop: D(1.4, 1, "jaw"),
    natural: D(2.2, 0.3, "nape", { texture: "curls" }),
    page: D(1, 1, "jaw"),
    page2: D(1, 0.9, "jaw"),
    part2: D(0.9, 0.4, "nape"),
    parted: D(0.9, 0.4, "nape"),
    pixie: D(0.8, 0.7, "nape"),
    plain: D(0.9, 0.35, "nape"),
    ponytail2: D(0.9, 0.35, "nape", { tie: "ponytail" }),
    princess: D(1, 0.4, "back", { tie: "topknot" }),
    sara: D(1.1, 0.5, "back"),
    shorthawk: D(0.4, 0.1, "nape", { sides: "shaved", top: "crest" }),
    shortknot: D(0.9, 0.35, "nape", { tie: "topknot" }),
    shoulderl: D(1, 0.6, "shoulder"),
    shoulderr: D(1, 0.6, "shoulder"),
    single: D(0.9, 0.35, "nape", { tie: "braid" }),
    spiked: D(0.8, 0.3, "nape", { top: "spikes" }),
    spiked2: D(0.8, 0.4, "nape", { top: "spikes" }),
    spiked_beehive: D(0.9, 0.3, "nape", { top: "beehive" }),
    spiked_liberty: D(0.6, 0.2, "nape", { top: "spikes", sides: "faded" }),
    spiked_liberty2: D(0.6, 0.2, "nape", { top: "spikes", sides: "faded" }),
    spiked_porcupine: D(0.8, 0.3, "nape", { top: "spikes" }),
    swoop: D(1, 0.7, "nape"),
    twists_fade: D(1.1, 0.3, "nape", { sides: "faded", texture: "locks" }),
    twists_straight: D(1.1, 0.3, "nape", { texture: "locks" }),
    unkempt: D(1.2, 0.6, "jaw", { top: "tufts" }),
    wavy: D(1.2, 0.5, "back", { texture: "curls" }),
    xlong: D(1, 0.4, "waist"),
}

/** The design for a look's hair style; none for bald (or `none`) and for a style it doesn't know, short. */
export const hairDesignFor = (style: string): HairDesign | undefined =>
    style === "bald" || style === "none" ? undefined : HAIR_DESIGNS[style] ?? HAIR_DESIGNS.short

/**
 * Hair from a design: a shell over the skull cut to its hairline, and what falls or stands off it.
 * Sizes are set for the LPC head (10.05 x 8.7 x 9.15) and scaled to this one's (`k`); features of
 * their own keep a few pixels on a small head (`s`), or a realist's spikes would be specks.
 */
const hairOf = (sk: Skeleton, p: Proportions, d: HairDesign, m: Material): Primitive[] => {
    const [a, b, c] = p.head
    const k = b / 8.7
    const s = Math.max(k, 0.6)
    const at = (v: Vec3) => add(sk.head, apply(sk.headBasis, v))
    const prims: Primitive[] = []
    const long = d.length === "shoulder" || d.length === "back" || d.length === "waist"
    // The cap: at least most of a pixel thick, so a small head's hair still shows past the skull.
    // Volume past a short cut's grows faster: an afro must double a small head, not pad it -- on a
    // head smaller than LPC's (a realist's) by more than the head's own scale, or a full style is a
    // cap. Long hair has body a short cut doesn't: it stands further off.
    const full = k < 1 ? Math.max(k * 1.3, 2) : k * 1.3
    const lift = Math.min(d.volume, 1) * Math.max(k, 0.7) + Math.max(0, d.volume - 1) * full + (long ? Math.max(k, 0.7) : 0)
    // Volume past a pixel or so grows up and back, off the face: inflated all round, a big head of
    // hair stands out in front of the face too, and the hairline cut leaves a brim over the eyes.
    const extra = Math.max(0, lift - 1.2)
    const center = at([0, 0.5 * k + 0.35 * extra, -0.4 * k - 0.45 * extra])
    const radii: Vec3 = [a + lift, b + lift - 0.25 * extra, c + lift - 0.45 * extra]
    // The hairline: above the brow at the front, lower around the back and sides.
    // (A small head's long hair comes lower, or front-on it is a band over a bare crown.)
    const brow = p.eyeHeight + b * (0.62 - 0.4 * d.fringe) - (k < 1 && long ? 0.8 : 0) - 0.35 * extra
    // A full head of hair (an afro, a mop) comes round the head, not just over it: a dome cut off
    // above the ears reads as a mushroom.
    const bushy = d.volume >= 1.4
    const nape = d.length === "nape" && !bushy ? -b * 0.5 : -b * 0.9
    // Hair to the jaw or longer comes down over the ears, framing the face.
    const side = d.length === "jaw" || long || bushy ? -b * 0.85 : brow - 3 * k
    // (Bushy hair stands round the face at the sides too: the face shows through a window in it.)
    const sides = (q: Vec3) => Math.abs(q[0]) > a * 0.8 && q[1] > side && (bushy || q[2] < c * 0.2)
    let keep = (q: Vec3) => q[1] > brow || (q[2] < -c * 0.15 && q[1] > nape) || sides(q)
    if (d.sides === "shaved") {
        const strip = keep
        keep = q => strip(q) && Math.abs(q[0]) < a * 0.38
    }
    if (d.sides === "faded") {
        // Close on the sides and back below the crown: a thin second cap there.
        const top = keep
        keep = q => top(q) && q[1] > b * 0.25
        prims.push(ellipsoid(center, sk.headBasis, inflate(p.head, 0.3 * Math.max(k, 0.7)), m, "hair", {
            exponent: p.headRoundness,
            keep: q => q[1] <= b * 0.3 && ((q[2] < -c * 0.15 && q[1] > -b * 0.5) || sides(q)),
        }))
    }
    if (d.crown === "bald") {
        const ring = keep
        keep = q => ring(q) && q[1] < b * 0.3 && q[2] < c * 0.1
    }
    prims.push(ellipsoid(center, sk.headBasis, radii, m, "hair", { exponent: p.headRoundness, keep }))
    if (long) {
        // Falling past the nape: a sheet from the back of the head down the back, as far as the
        // length goes -- to the shoulders, the shoulder blades, the waist -- measured on the body, not
        // the head (sized off a small head it hid inside the neck), standing off the back and a shirt
        // over it. Seen from the front it shows either side of the neck, behind the shoulders.
        const torso = sk.torsoBasis
        const top = dot(sub(sk.head, sk.chest), apply(torso, [0, 1, 0]))
        const end = d.length === "shoulder" ? p.shoulderHeight - 0.5 : d.length === "back" ? -p.torso[1] * 0.35 : -p.spine * 0.8
        const behind = p.torso[2] + 1.2
        prims.push(ellipsoid(add(sk.chest, apply(torso, [0, (top + end) / 2, -behind])), torso,
            [a + lift, (top - end) / 2, 1.4], m, "hair", { exponent: [2, 3.2, 2] }))
        // And a lock from each temple down past the jaw and forward over the shoulder, framing the
        // face (behind the shoulder it hid there, front-on).
        for (const x of [-1, 1]) {
            const temple = at([x * a * 0.9, -b * 0.1, -c * 0.05])
            const shoulder = add(sk.chest, apply(torso, [x * (p.neckRadius + 1.6), p.shoulderHeight + 0.6, p.torso[2] * 0.15]))
            prims.push(capsule(temple, shoulder, Math.max(1.3, 1.4 * k) + lift * 0.3, m, "hair"))
        }
    }
    if (d.length === "jaw") {
        // Framing the face: a fall over each ear to the jaw.
        for (const x of [-1, 1]) prims.push(ellipsoid(at([x * a * 0.82, -b * 0.45, -c * 0.15]), sk.headBasis, [Math.max(1.6 * s, 1.3) + lift * 0.5, b * 0.55, c * 0.6], m, "hair"))
    }
    // On top.
    const spike = (dir: Vec3, length: number, r: number) => {
        const out = normalize(apply(sk.headBasis, dir))
        const base = add(center, scale(out, b * 0.75))
        prims.push(segment(base, add(base, scale(out, length)), r, m, "hair"))
    }
    if (d.top === "spikes") for (const [x, z] of [[0, 0.5], [-0.6, 0.15], [0.6, 0.15], [-0.4, -0.45], [0.4, -0.45], [0, -0.1]]) spike([x, 1, z], 6.5 * s, 1.6 * s)
    if (d.top === "tufts") for (const [x, z] of [[-0.45, 0.3], [0.35, 0.4], [0.1, -0.35], [-0.25, -0.1]]) spike([x, 1, z], 3 * s, 1.4 * s)
    if (d.top === "tuft" || d.top === "tall-tuft") spike([0.1, 1, 0.6], (d.top === "tuft" ? 2.5 : 4.5) * s, 1.3 * s)
    if (d.top === "crest" || d.top === "tall-crest") {
        const tall = d.top === "crest" ? 2.4 : 4.5
        prims.push(ellipsoid(at([0, b + lift + tall * s * 0.4, -c * 0.1]), sk.headBasis, [1.3 * s, tall * s, c * 0.85], m, "hair"))
    }
    if (d.top === "flat") prims.push(ellipsoid(at([0, b * 0.85 + lift, -c * 0.05]), sk.headBasis, [a * 0.88, 2.2 * s, c * 0.82], m, "hair", { exponent: [2.6, 6, 2.6] }))
    if (d.top === "quiff") prims.push(ellipsoid(at([0, b * 0.85 + lift + 0.8 * s, c * 0.55]), sk.headBasis, [a * 0.75, 3.2 * s, 3.8 * s], m, "hair"))
    if (d.top === "beehive") prims.push(ellipsoid(at([0, b * 1.25 + lift, -c * 0.15]), sk.headBasis, [a * 0.8, b * 0.8, c * 0.8], m, "hair"))
    // Tied.
    if (d.tie === "ponytail") {
        const root = at([0, 2.5 * k, -c - 0.6 * k])
        prims.push(capsule(root, add(root, apply(sk.torsoBasis, [0, -10 * k, -2.2 * k])), 1.9 * k, m, "hair"))
    }
    if (d.tie === "high-ponytail") {
        const root = at([0, b * 0.75, -c * 0.85])
        prims.push(capsule(root, add(root, apply(sk.torsoBasis, [0, -12 * s, -2.6 * s])), 1.8 * s, m, "hair"))
    }
    if (d.tie === "bun") prims.push(ellipsoid(at([0, b * 0.25, -c - 1.4 * s]), sk.headBasis, [2.6 * s, 2.6 * s, 2.4 * s], m, "hair"))
    if (d.tie === "topknot") prims.push(ellipsoid(at([0, b + lift + 1.6 * s, -c * 0.25]), sk.headBasis, [2.3 * s, 2.2 * s, 2.3 * s], m, "hair"))
    if (d.tie === "bunches") {
        for (const x of [-1, 1]) {
            const root = at([x * (a + 0.4 * s), b * 0.15, -c * 0.35])
            prims.push(capsule(root, add(root, apply(sk.headBasis, [x * 2.2 * s, -8 * s, -0.8 * s])), 1.6 * s, m, "hair"))
        }
    }
    if (d.tie === "braid") {
        // Plaited: a chain of knots down the back.
        for (let i = 0; i < 6; i++) prims.push(ellipsoid(add(at([0, -b * 0.4, -c - 0.4 * s]), apply(sk.torsoBasis, [0, -i * 2.2 * s, -0.3 * i * s])), sk.headBasis, [1.4 * s, 1.3 * s, 1.3 * s], m, "hair"))
    }
    if (!d.texture) return prims
    // Texture, painted on: curls a broken speckle, locks and rows strands a pixel apart.
    const up = apply(sk.headBasis, [0, 1, 0]), across = apply(sk.headBasis, [1, 0, 0]), ahead = apply(sk.headBasis, [0, 0, 1])
    return painted(prims, w => {
        const v = sub(w, sk.head)
        if (d.texture === "curls") {
            // Scattered: a linear pattern of the cells lines up into stripes across a curved shell.
            const [i, j, l] = [Math.floor(dot(v, across) / 1.3), Math.floor(dot(v, up) / 1.3), Math.floor(dot(v, ahead) / 1.3)]
            let h = Math.imul(i, 374761393) + Math.imul(j, 668265263) + Math.imul(l, 1274126177)
            h = Math.imul(h ^ (h >>> 13), 1274126177)
            return ((h ^ (h >>> 16)) >>> 0) % 4 === 0 ? m.deep : undefined
        }
        if (d.texture === "rows") return ((dot(v, across) / 1.6) % 1 + 1) % 1 < 0.35 && dot(v, up) > 0 ? m.deep : undefined
        const around = Math.atan2(dot(v, ahead), dot(v, across)) * (a + lift)
        return ((around / 1.8) % 1 + 1) % 1 < 0.3 ? m.deep : undefined
    })
}

const LACE = rampFrom("#e6dcc4")

/**
 * A shirt: the body's own chest and hips inflated, with sleeves to the elbow or the wrist. Painted
 * on (and lit here, decals skipping the raster's light): a neckline, which shows the skin beneath
 * -- a round scoop; a low one leaving the shoulders bare but for straps; a V laced across -- and a
 * pattern, sailor stripes or a corset's lacing. Puffed shoulders stand the sleeve tops out; a
 * tunic's hem to mid-thigh is a short skirt (skirtLike).
 */
const shirtOf = (sk: Skeleton, p: Proportions, shirt: NonNullable<EmberLook["shirt"]>, t: number, facing: Facing,
    skin: Material): Primitive[] => {
    const m = dye(shirt.dye)
    const shoulders = upperBody(sk, p, m, 0.6 * t, facing)
    // Sleeveless leaves the deltoids bare.
    let body: Primitive[] = shirt.sleeves === "none" ? shoulders.filter(s => s.group === "torso" || s.group === "bust") : shoulders
    // A shirt reaches the belt; a tunic hangs over the hips. On a body with a waist the shirt ends
    // at the waist's narrowest (where leg wear begins) and hugs it: carried down over the top of
    // the hips, its hem stood out at the belly and read as one.
    const top = p.waist ? (p.pelvisLow ?? 0) + (p.waistHeight ?? 0) : -p.pelvis[1] * 0.1
    body = [...body, ...lowerBody(sk, p, m, (shirt.tunic ? 1.1 : 0.85) * t, shirt.tunic ? undefined : q => q[1] > top)]
    if (shirt.neck || shirt.pattern) body = painted(body, shirtPaint(sk, p, shirt, facing, skin))
    const prims = [...body]
    for (const side of SIDES) {
        const j = sk[side]
        const puff = shirt.puffed ? 0.5 + 0.6 * t : 0
        if (shirt.sleeves === "short") {
            prims.push(segment(j.shoulder, lerp(j.shoulder, j.elbow, 0.6), p.upperArmRadius + 0.5 * t + puff, m, armOf(side)))
        } else if (shirt.sleeves === "long") {
            prims.push(segment(j.shoulder, j.elbow, p.upperArmRadius + 0.5 * t, m, armOf(side)))
            prims.push(joint(j.elbow, p.forearmRadius * 0.85 + 0.45 * t, m, armOf(side)))
            prims.push(segment(j.elbow, lerp(j.elbow, j.hand, 0.8), p.forearmRadius + 0.45 * t, m, armOf(side)))
            if (puff) prims.push(segment(j.shoulder, lerp(j.shoulder, j.elbow, 0.4), p.upperArmRadius + 0.5 * t + puff, m, armOf(side)))
        }
    }
    if (shirt.hem === "thigh") prims.push(...skirtLike(sk, p, m, t, 0.45, { m }))
    return prims
}

/** A shirt's neckline and pattern, painted by where a point is on the torso. */
const shirtPaint = (sk: Skeleton, p: Proportions, shirt: NonNullable<EmberLook["shirt"]>, facing: Facing, skin: Material) => {
    const lit = lighting(facing)
    const up = apply(sk.torsoBasis, [0, 1, 0]), across = apply(sk.torsoBasis, [1, 0, 0]), ahead = apply(sk.torsoBasis, [0, 0, 1])
    // The collar: the top of the chest, where a crew neck sits.
    const collar = p.torso[1] * 0.92
    const white = dye("white")
    // Criss-crossed lacing, a strand every couple of pixels.
    const laced = (x: number, y: number) => (((y + Math.abs(x) * 1.6) / 2.0) % 1 + 1) % 1 < 0.34
    return (w: Vec3, n: Vec3): number | undefined => {
        const v = sub(w, sk.chest)
        const x = dot(v, across), y = dot(v, up), front = dot(v, ahead) > 0
        const step = lit(n)
        if (shirt.neck === "scoop" || shirt.neck === "low") {
            const low = shirt.neck === "low"
            const width = p.shoulderWidth * (low ? 0.55 : 0.42), depth = p.torso[1] * (low ? 0.45 : 0.28)
            if (front && Math.abs(x) < width && y > collar - depth * (1 - (x / width) ** 2)) return tone(skin, step)
            // Low: bare shoulders, the shirt hanging from a strap each side.
            if (low && Math.abs(x) > width + 1.4 && y > collar - p.torso[1] * 0.3) return tone(skin, step)
        }
        if (shirt.neck === "laced" && front) {
            // Deep enough to read: a V a few pixels wide at the collar, laced across.
            const depth = p.torso[1] * 0.75
            if (Math.abs(x) < (y - (collar - depth)) * 0.5) return laced(x, y) ? tone(LACE, step) : tone(skin, step)
        }
        if (shirt.pattern === "stripes" && (((y / 1.8) % 1) + 1) % 1 < 0.45) return tone(white, step)
        if (shirt.pattern === "lacing" && front && Math.abs(x) < 1.7 && y < collar - p.torso[1] * 0.4 && laced(x, y)) return tone(LACE, step)
        return undefined
    }
}

/** Shirts Ember can draw, by the game's shirt item type, as the look's shirt (but its dye). */
const EMBER_SHIRTS: Record<string, Omit<NonNullable<EmberLook["shirt"]>, "dye">> = {
    longsleeve: { sleeves: "long" },
    longsleeve_laced: { sleeves: "long", neck: "laced" },
    sleeveless: { sleeves: "none", neck: "scoop" },
    sleeveless_laced: { sleeves: "none", neck: "laced" },
    sleeveless_striped: { sleeves: "none", neck: "scoop", pattern: "stripes" },
    blouse: { sleeves: "short", neck: "scoop", puffed: true },
    blouse_longsleeve: { sleeves: "long", neck: "scoop", puffed: true },
    corset: { sleeves: "none", neck: "low", pattern: "lacing" },
    scoop_shirt: { sleeves: "short", neck: "scoop" },
    tanktop: { sleeves: "none", neck: "low" },
    tunic: { sleeves: "none", tunic: true, hem: "thigh" },
}
export const EMBER_SHIRT_NAMES = Object.keys(EMBER_SHIRTS)
/** The look's shirt for a worn shirt item of `type` in `color`. */
export const wornShirt = (type: string, color: string): EmberLook["shirt"] => ({ dye: color, ...EMBER_SHIRTS[type] })

/** How footwear is made (EmberLook.shoes). */
export type ShoeCut = "shoes" | "boots" | "slippers" | "sandals" | "sabatons"

/**
 * Footwear over the foot's own volume. Shoes cover it to the ankle; boots climb the shin; slippers
 * are a sole and a toe box, the instep bare; sandals a sole and straps criss-crossing a bare foot,
 * tied round the ankle; sabatons are steel, in plates across the foot, with metal's own light.
 */
const footwearOf = (sk: Skeleton, p: Proportions, shoes: NonNullable<EmberLook["shoes"]>, t: number, footWidth: number,
    facing: Facing): Primitive[] => {
    const cut = shoes.cut ?? "shoes"
    const m = cut === "sabatons" ? metalFrom(ARMOR_COLORS.plate[shoes.dye] ?? ARMOR_COLORS.plate.metal) : dye(shoes.dye)
    const prims: Primitive[] = []
    for (const side of SIDES) {
        const j = sk[side]
        const g = legOf(side)
        const shoe = (by: number, keep?: (q: Vec3) => boolean): Ellipsoid => ({
            ...foot(j.heel, j.toe, p.footRadius + by, footWidth + by, m, g),
            ...(keep ? { keep } : {}),
        })
        if (cut === "shoes") {
            prims.push(shoe(0.5 * t))
        } else if (cut === "boots") {
            prims.push(shoe(0.5 * t), capsule(j.ankle, lerp(j.ankle, j.knee, 0.45), p.shinRadius + 0.8 * t, m, g))
        } else if (cut === "slippers") {
            // Low all round, and a toe box over the front: the top of the foot behind it shows.
            const r = p.footRadius + 0.35 * t
            prims.push(shoe(0.35 * t, q => q[1] < r * 0.1 || q[2] > 0))
        } else if (cut === "sandals") {
            // The sole under the foot; straps crossing over it, the skin between them. Stood well
            // off the foot and over a pixel wide, or a small foot's straps never win a pixel.
            const by = 0.5 + 0.3 * t
            const r = p.footRadius + by
            const strap = (q: Vec3) => (((q[2] + Math.abs(q[0]) * 1.2) / 2.4) % 1 + 1) % 1 < 0.5
            prims.push(shoe(by, q => q[1] < -r * 0.35 || strap(q)))
            // Tied round the ankle: a band of a shell, the ankle inside it.
            const tie = ellipsoid(j.ankle, sk.pelvisBasis, [p.shinRadius * 0.8 + 0.8, 1.6, p.shinRadius * 0.8 + 0.8], m, g, {
                keep: q => Math.abs(q[1]) < 0.7,
            })
            prims.push(tie)
        } else {
            // Steel plates across the foot, overlapping toward the toe, and a cuff over the ankle.
            const along = normalize(sub(j.toe, j.heel))
            const steel = [shoe(0.6 * t), capsule(j.ankle, lerp(j.ankle, j.knee, 0.22), p.shinRadius + 0.7 * t, m, g)]
            const shine = sheen(m, facing)
            prims.push(...painted(steel, (w, n) => {
                const d = dot(sub(w, j.heel), along) / 1.7
                return d > 1 && d - Math.floor(d) < 0.3 ? m.deep : shine(n)
            }))
        }
    }
    return prims
}

/** Footwear Ember can draw, by the game's shoes item type. */
const EMBER_SHOES: Record<string, ShoeCut> = { shoes: "shoes", boots: "boots", slippers: "slippers", sandals: "sandals", "shoes-armor": "sabatons" }
export const EMBER_SHOES_NAMES = Object.keys(EMBER_SHOES)
/** The look's shoes for a worn shoes item of `type` in `color`. */
export const wornShoes = (type: string, color: string): EmberLook["shoes"] => ({ dye: color, cut: EMBER_SHOES[type] })

/** The dark of a skull's sockets and the hollow behind the ribs. */
const HOLLOW = rampFrom("#3b3330")

/**
 * A skeleton on the same rig as a living body, so every clip, weapon and garment works on it: a
 * skull with dark sockets, nose and teeth; a ribcage of bands with the hollow dark behind them, a
 * sternum and collarbones; a spine down to a pelvic bowl; limb bones with knobbed joints; bony
 * hands and feet. Bone is a few pixels of ivory at this size: every bone stays two or three pixels
 * thick (forearm and shin are one bone each -- a pair is too fine to read), and the skull is drawn
 * large, as pixel-art skeletons are, so its sockets and teeth show.
 */
const bonesOf = (sk: Skeleton, p: Proportions, facing: Facing, BONE: Material): Primitive[] => {
    const prims: Primitive[] = []
    const lit = lighting(facing)
    const [a, b, c] = p.head
    // ---- skull -------------------------------------------------------------------------------
    const cranium = ellipsoid(add(sk.head, apply(sk.headBasis, [0, p.craniumLift + 0.9, -0.2])), sk.headBasis, [a * 1.25, b * 1.18, c * 1.12], BONE, "head", {
        exponent: p.headRoundness,
    })
    const jaw = ellipsoid(add(sk.head, apply(sk.headBasis, [0, -b * 0.62, c * 0.38])), sk.headBasis, [a * 0.9, b * 0.48, c * 0.6], BONE, "head", {
        exponent: [2.4, 2.6, 2],
    })
    const across = apply(sk.headBasis, [1, 0, 0]), up = apply(sk.headBasis, [0, 1, 0]), ahead = apply(sk.headBasis, [0, 0, 1])
    // Sockets, the nose and the teeth by their direction from the skull's centre and how wide they
    // look from it: on the shell wherever it is, so a profile shows the near socket at the front of
    // the skull too. Sized so the two sockets stay apart on a small head.
    const skull = add(sk.head, apply(sk.headBasis, [0, p.craniumLift + 0.9, -0.2]))
    const toward = (x: number, y: number) => normalize(apply(sk.headBasis, [x, y - p.craniumLift - 0.9, c]))
    // Side-on the head is turned nearly to profile: sockets that far round would sit on the
    // skull's outline, so there they are set further back, the near one inside the face.
    const profile = facing === "left" || facing === "right"
    const eyeY = p.eyeHeight + b * 0.1, eyeX = Math.max(a * (profile ? 0.9 : 0.5), 1.7)
    const sockets = [toward(-eyeX, eyeY), toward(eyeX, eyeY)]
    const socketWide = Math.cos(Math.atan2(Math.max(a * (profile ? 0.42 : 0.3), profile ? 1.6 : 1.2), c))
    const nose = toward(0, eyeY - Math.max(a * 0.4, 1.5)), noseWide = Math.cos(Math.atan2(Math.max(a * 0.13, 0.6), c))
    const teeth = -b * 0.52
    prims.push(...painted([cranium, jaw], (w, n) => {
        const from = normalize(sub(w, skull))
        // Holes take no light: always the darkest.
        if (sockets.some(d => dot(from, d) > socketWide) || dot(from, nose) > noseWide) return HOLLOW.deep
        const v = sub(w, sk.head)
        const x = dot(v, across), y = dot(v, up)
        if (dot(v, ahead) > c * 0.3 && Math.abs(y - teeth) < Math.max(0.55, b * 0.09) && Math.abs(x) < a * 0.6 && ((x / 1.1) % 1 + 1) % 1 < 0.4) {
            return HOLLOW.base
        }
        return undefined
    }))
    // ---- neck and spine ----------------------------------------------------------------------
    const neckTop = add(sk.head, apply(sk.headBasis, [0, -b * 0.55, -c * 0.25]))
    const neckBase = add(sk.chest, apply(sk.torsoBasis, [0, p.torso[1] * 0.85, -p.torso[2] * 0.3]))
    for (let i = 0; i < 3; i++) prims.push(joint(lerp(neckBase, neckTop, (i + 0.5) / 3), 1.1, BONE, "torso"))
    const lumbarTop = add(sk.chest, apply(sk.torsoBasis, [0, -p.torso[1] * 0.55, -p.torso[2] * 0.35]))
    const sacrum = add(sk.pelvis, apply(sk.pelvisBasis, [0, p.pelvis[1] * 0.3, -p.pelvis[2] * 0.4]))
    for (let i = 0; i < 4; i++) prims.push(ellipsoid(lerp(lumbarTop, sacrum, (i + 0.5) / 4), sk.torsoBasis, [1.4, 0.9, 1.2], BONE, "torso"))
    // ---- ribcage -----------------------------------------------------------------------------
    const [tx, ty, tz] = p.torso
    const ribsAt = add(sk.chest, apply(sk.torsoBasis, [0, ty * 0.15, p.posture]))
    const ribRadii: Vec3 = [tx * 0.82, ty * 0.78, tz * 0.88]
    const period = Math.max(1.9, ty * 0.3)
    prims.push(ellipsoid(ribsAt, sk.torsoBasis, ribRadii, BONE, "torso", {
        exponent: p.torsoRoundness,
        // Bands of rib down to the floating ribs, open below.
        keep: q => q[1] > -ribRadii[1] * 0.7 && (((q[1] + ribRadii[1]) / period) % 1) < 0.55,
    }))
    // The hollow behind the ribs, dark between them.
    prims.push(ellipsoid(ribsAt, sk.torsoBasis, [ribRadii[0] * 0.8, ribRadii[1] * 0.8, ribRadii[2] * 0.75], HOLLOW, "torso", {
        exponent: p.torsoRoundness,
        keep: q => q[1] > -ribRadii[1] * 0.55,
    }))
    // Sternum down the front; collarbones out to the shoulders.
    const sternumTop = add(ribsAt, apply(sk.torsoBasis, [0, ribRadii[1] * 0.75, ribRadii[2] * 0.75]))
    prims.push(capsule(sternumTop, add(ribsAt, apply(sk.torsoBasis, [0, -ribRadii[1] * 0.2, ribRadii[2] * 0.95])), 0.8, BONE, "torso"))
    for (const side of SIDES) prims.push(capsule(sternumTop, sk[side].shoulder, 0.75, BONE, "torso"))
    // ---- pelvis: an open bowl ----------------------------------------------------------------
    const [px, py, pz] = p.pelvis
    prims.push(ellipsoid(add(sk.pelvis, apply(sk.pelvisBasis, [0, py * 0.15, 0])), sk.pelvisBasis, [px * 0.95, py * 0.55, pz * 0.62], BONE, "torso", {
        exponent: [2.4, 2.2, 2.2],
        keep: q => q[1] < py * 0.25,
    }))
    // ---- limbs -------------------------------------------------------------------------------
    for (const side of SIDES) {
        const j = sk[side]
        const arm = armOf(side), leg = legOf(side)
        prims.push(as(joint(j.shoulder, Math.max(1.5, p.upperArmRadius * 0.62), BONE, arm), "upperArm"))
        prims.push(as(capsule(j.shoulder, j.elbow, Math.max(1.25, p.upperArmRadius * 0.5), BONE, arm), "upperArm"))
        prims.push(as(joint(j.elbow, Math.max(1.4, p.forearmRadius * 0.68), BONE, arm), "forearm"))
        prims.push(as(capsule(j.elbow, j.hand, Math.max(1.1, p.forearmRadius * 0.52), BONE, arm), "forearm"))
        prims.push(as(ellipsoid(fistCenter(sk, p, side), sk.torsoBasis, [p.handRadius * 0.6, p.handRadius * 0.7, p.handRadius * 0.6], BONE, arm, { exponent: 2.6 }), "hand"))
        prims.push(as(joint(j.hip, Math.max(1.6, p.thighRadius * 0.55), BONE, leg), "thigh"))
        prims.push(as(capsule(j.hip, j.knee, Math.max(1.35, p.thighRadius * 0.47), BONE, leg), "thigh"))
        prims.push(as(joint(j.knee, Math.max(1.5, p.shinRadius * 0.68), BONE, leg), "shin"))
        prims.push(as(capsule(j.knee, j.ankle, Math.max(1.15, p.shinRadius * 0.5), BONE, leg), "shin"))
        prims.push(as(joint(j.ankle, Math.max(0.9, p.shinRadius * 0.4), BONE, leg), "foot"))
        prims.push(as(foot(j.heel, j.toe, p.footRadius * 0.75, (facing === "left" || facing === "right" ? p.footProfileWidth : p.footWidth) * 0.75, BONE, leg), "foot"))
    }
    return prims
}

/** Shields Ember can draw, by the game's shield item type. */
export const EMBER_SHIELD_NAMES = ["shield-crusader", "shield-kite", "shield-kite_blue", "shield-kite_gray", "shield-kite_green", "shield-kite_red", "shield-round", "shield-spartan"]

/** Body armour Ember can draw, by the game's armor item type. */
const EMBER_ARMOR: Record<string, ArmorKind> = { "armor-leather": "leather", "armor-legion": "legion", "armor-plate": "plate" }
export const EMBER_ARMOR_NAMES = Object.keys(EMBER_ARMOR)
/** The look's armour for a worn armor item of `type` in `color`. */
export const wornArmor = (type: string, color: string): EmberLook["armor"] => ({ kind: EMBER_ARMOR[type], color })

/** Bauldrons Ember can draw, by the game's bauldron item type. */
export const EMBER_BAULDRON_NAMES = ["bauldron"]

/** How a pair of leg wear is cut (EmberLook.pants). */
export type PantsCut = "trousers" | "shorts" | "pantaloons" | "skirt" | "legion" | "greaves"

/** Leg wear Ember can draw, by the game's pants item type, as the look's pants cut. */
const EMBER_PANTS: Record<string, PantsCut> = { pants: "trousers", pantaloons: "pantaloons", skirts: "skirt", legion: "legion", "pants-armor": "greaves" }
export const EMBER_PANTS_NAMES = Object.keys(EMBER_PANTS)
/** The look's pants for a worn pants item of `type` in `color`. */
export const wornPants = (type: string, color: string): EmberLook["pants"] => ({ dye: color, cut: EMBER_PANTS[type] })

type HeldDrawing = (sk: Skeleton, p: Proportions, color?: string) => Primitive[]
const inRight = (draw: (j: LimbJoints, fist: Vec3, p: Proportions, color?: string) => Primitive[]): HeldDrawing =>
    (sk, p, color) => draw(sk.R, fistCenter(sk, p, "R"), p, color)

/** Things a hand can hold, by the name an EmberLook's `held` gives. */
const EMBER_HELD: Record<string, HeldDrawing> = {
    axe: inRight(axe),
    dagger: inRight(dagger),
    bow,
    longsword: inRight(longsword),
    saber: inRight(saber),
    mace: inRight(mace),
    warhammer: inRight(warhammer),
    flail: inRight(flail),
    waraxe: inRight(waraxe),
    halberd: inRight(halberd),
    scythe: inRight(scythe),
    rapier: inRight(rapier),
    "iron-pickaxe": inRight(pickaxe),
    spear: inRight(spear),
    staff: inRight(staff),
    crossbow: inRight(crossbow),
}

/** Held in both hands: a shield goes on the back (generate.ts shieldSlung). */
export const TWO_HANDED = ["bow", "waraxe", "halberd", "scythe", "spear", "staff", "crossbow"]
/** Long enough to be carried upright, the butt toward the ground (clips.ts poleCarry). */
export const POLEARMS = ["halberd", "scythe", "spear", "staff"]
export const EMBER_HELD_NAMES = Object.keys(EMBER_HELD)

export const buildPrimitives = (
    sk: Skeleton,
    p: Proportions,
    look: EmberLook,
    facing: Facing = "front",
    skins: Record<string, Material> = SKIN,
): Primitive[] => {
    const skin = skins[look.skin] ?? skins.light
    const prims: Primitive[] = []
    const footWidth = facing === "left" || facing === "right" ? p.footProfileWidth : p.footWidth

    // ---- body -------------------------------------------------------------------------------
    if (look.bones) {
        prims.push(...bonesOf(sk, p, facing, BONES[look.boneColor ?? "white"] ?? BONE))
    } else {
        const bare = fleshOf(sk, p, skin, facing, footWidth, look.beast)
        const flesh = look.beast ? furred(bare, skin, facing) : bare
        prims.push(...(look.wounds ? painted(flesh, woundsOf(sk, p, facing)) : flesh))
    }
    prims.push(...dressed(sk, p, look, facing, skin, footWidth))
    if (look.bauldron) {
        // Its strap lies on whichever of the torso's surfaces is outermost: paint it on them all.
        const strap = bauldronStrap(sk, p, rampFrom(BAULDRON_COLORS[look.bauldron.color] ?? BAULDRON_COLORS.brown), facing)
        const torso = (prim: Primitive) => prim.group === "torso" || prim.group === "bust" || prim.group === "shoulder"
        return prims.map(prim => (torso(prim) ? overPainted([prim], strap)[0] : prim))
    }
    return prims
}

/**
 * A muscled belly's six-pack, painted down the front of the bare chest below the pectorals: the
 * line down the middle and two across, a shade darker than the skin round them.
 */
const absOf = (sk: Skeleton, p: Proportions, skin: Material, facing: Facing) => {
    const lit = lighting(facing)
    const up = apply(sk.torsoBasis, [0, 1, 0]), across = apply(sk.torsoBasis, [1, 0, 0]), ahead = apply(sk.torsoBasis, [0, 0, 1])
    const top = p.torso[1] * 0.0, bottom = -p.torso[1] * 0.8, half = p.torso[0] * 0.3
    return (w: Vec3, n: Vec3): number | undefined => {
        const v = sub(w, sk.chest)
        const x = dot(v, across), y = dot(v, up)
        if (dot(v, ahead) <= 0 || y > top || y < bottom || Math.abs(x) > half) return undefined
        const row = (top - y) / ((top - bottom) / 3)
        const line = Math.abs(x) < 0.45 || (row % 1 < 0.22 && row > 0.5)
        return line ? tone(skin, Math.min(3, lit(n) + 1)) : undefined
    }
}

/** The living body: head, chest, hips and limbs as muscle-shaped volumes in the skin. */
/**
 * Fur on a beast's body: short strands a shade darker than the light gives each spot, scattered
 * over every volume. Hashed in each volume's own frame, so the grain moves with the body instead of
 * crawling over it as it walks; paint already on a volume (a muscled chest's abs) shows first.
 */
const furred = (prims: Primitive[], fur: Material, facing: Facing): Primitive[] => {
    const lit = lighting(facing)
    return prims.map(prim => {
        if (prim.kind !== "ellipsoid") return prim
        const e = typeof prim.exponent === "number" ? [prim.exponent, prim.exponent, prim.exponent] : prim.exponent ?? [2, 2, 2]
        const before = prim.decal
        return {
            ...prim,
            decal: (q: Vec3) => {
                const painted = before?.(q)
                if (painted !== undefined) return painted
                // Strands: cells taller than wide, about one in five drawn.
                let h = Math.imul(Math.floor(q[0] / 1.3), 374761393) + Math.imul(Math.floor(q[1] / 2.4), 668265263) + Math.imul(Math.floor(q[2] / 1.3), 1274126177)
                h = Math.imul(h ^ (h >>> 13), 1274126177)
                if (((h ^ (h >>> 16)) >>> 0) % 5 !== 0) return undefined
                const g = q.map((v, i) => (Math.sign(v) * Math.pow(Math.abs(v) / prim.radii[i], e[i] - 1)) / prim.radii[i]) as Vec3
                return tone(fur, Math.min(3, lit(normalize(apply(prim.basis, g))) + 1))
            },
        }
    })
}

const fleshOf = (sk: Skeleton, p: Proportions, skin: Material, facing: Facing, footWidth: number, beast?: EmberLook["beast"]): Primitive[] => {
    const prims: Primitive[] = []
    prims.push(...(beast === "wolf" ? [...wolfHeadOf(sk, p, skin, facing), ...wolfFurOf(sk, p, skin)] : head(sk, p, skin)))
    prims.push(capsule(sk.chest, sk.neck, p.neckRadius, skin, "torso"))
    const chest = upperBody(sk, p, skin, 0, facing)
    // A muscled chest's abs, on the chest volume itself (the first), under any shirt.
    prims.push(...(p.pecs ? [...painted(chest.slice(0, 1), absOf(sk, p, skin, facing)), ...chest.slice(1)] : chest))
    prims.push(...lowerBody(sk, p, skin))
    for (const side of SIDES) {
        const j: LimbJoints = sk[side]
        // Each joint ball belongs to the segment below it, so a joint gets one line, not two.
        prims.push(as(segment(j.shoulder, j.elbow, p.upperArmRadius, skin, armOf(side)), "upperArm"))
        prims.push(as(joint(j.elbow, p.forearmRadius * 0.85, skin, armOf(side)), "forearm"))
        prims.push(as(segment(j.elbow, j.hand, p.forearmRadius, skin, armOf(side)), "forearm"))
        if (p.handLength) {
            // A closed fist on the end of the forearm: a squarish knob, wider than the wrist.
            const tip = add(j.hand, scale(normalize(sub(j.hand, j.elbow)), p.handLength))
            prims.push(as(segment(j.hand, tip, p.handRadius, skin, armOf(side), 2.8), "hand"))
        } else {
            const fist = add(j.hand, apply(sk.torsoBasis, [-sideSign(side) * p.handInset, 0, 0]))
            prims.push(as(ellipsoid(fist, sk.torsoBasis, [p.handRadius, p.handRadius, p.handRadius], skin, armOf(side)), "hand"))
        }
        prims.push(...thighOf(sk, side, p, 0, skin, legOf(side)).map(prim => as(prim, "thigh")))
        prims.push(as(joint(j.knee, p.shinRadius * 0.9, skin, legOf(side)), "shin"))
        prims.push(...shinOf(sk, side, p, 0, skin, legOf(side)).map(prim => as(prim, "shin")))
        prims.push(as(joint(j.ankle, p.shinRadius * 0.7, skin, legOf(side)), "foot"))
        prims.push(as(foot(j.heel, j.toe, p.footRadius, footWidth, skin, legOf(side)), "foot"))
    }
    return prims
}

const WOUND = rampFrom("#8a3a34")
const GORE = rampFrom("#5a1f22")

/**
 * Where a dead body is torn open: a few wounds at fixed places on it -- the chest, a forearm, the
 * belly, a thigh, a shin, the back -- each a dark core in a raw rim, lit like the flesh round them.
 * Placed on the skeleton, so they stay put as the body moves and show from whichever side faces them.
 */
const woundsOf = (sk: Skeleton, p: Proportions, facing: Facing) => {
    const lit = lighting(facing)
    const T = (v: Vec3) => add(sk.chest, apply(sk.torsoBasis, v))
    const P = (v: Vec3) => add(sk.pelvis, apply(sk.pelvisBasis, v))
    const r = Math.max(1.2, p.torso[0] * 0.2)
    const wounds: [Vec3, number][] = [
        [T([p.torso[0] * 0.45, p.torso[1] * 0.3, p.torso[2]]), r],
        [T([-p.torso[0] * 0.3, -p.torso[1] * 0.4, p.torso[2]]), r * 0.8],
        [T([p.torso[0] * 0.2, p.torso[1] * 0.1, -p.torso[2]]), r],
        [lerp(sk.R.elbow, sk.R.hand, 0.5), r * 0.75],
        [P([p.hipWidth, -p.thigh * 0.35, p.thighRadius * 0.8]), r * 0.8],
        [lerp(sk.L.knee, sk.L.ankle, 0.4), r * 0.7],
    ]
    return (w: Vec3, n: Vec3): number | undefined => {
        for (const [at, size] of wounds) {
            const d = distance(w, at)
            if (d < size * 0.55) return tone(GORE, lit(n))
            if (d < size) return tone(WOUND, lit(n))
        }
        return undefined
    }
}

/** What the body has on: clothes, armour, hair, what it holds. */
const dressed = (sk: Skeleton, p: Proportions, look: EmberLook, facing: Facing, skin: Material, footWidth: number): Primitive[] => {
    const prims: Primitive[] = []

    // ---- garments: inflated copies of what they cover -----------------------------------------
    // How far cloth stands off the skin, scaled for bodies slimmer than LPC's.
    const t = p.clothThickness ?? 1
    if (look.pants?.cut === "pantaloons") {
        prims.push(...pantaloonsOf(sk, p, dye(look.pants.dye), t))
    } else if (look.pants?.cut === "skirt") {
        prims.push(...skirtOf(sk, p, dye(look.pants.dye), t))
    } else if (look.pants?.cut === "legion") {
        prims.push(...legionOf(sk, p, t))
    } else if (look.pants?.cut === "greaves") {
        prims.push(...greavesOf(sk, p, look.pants.dye, t, facing))
    } else if (look.pants) {
        const m = dye(look.pants.dye)
        prims.push(...lowerBody(sk, p, m, 0.5 * t, undefined, true))
        for (const side of SIDES) {
            const j = sk[side]
            prims.push(...thighOf(sk, side, p, 0.45 * t, m, legOf(side)))
            prims.push(joint(j.knee, p.shinRadius * 0.9 + 0.4 * t, m, legOf(side)))
            if (look.pants.cut !== "shorts") {
                prims.push(...shinOf(sk, side, p, 0.4 * t, m, legOf(side), 0.92))
            }
        }
    }
    if (look.shoes) prims.push(...footwearOf(sk, p, look.shoes, t, footWidth, facing))
    if (look.shirt) prims.push(...shirtOf(sk, p, look.shirt, t, facing, skin))
    if (look.belt) {
        const m = dye(look.belt.dye)
        const waist = add(sk.pelvis, apply(sk.pelvisBasis, [0, p.pelvis[1] * 0.55, 0]))
        prims.push(
            ellipsoid(waist, sk.pelvisBasis, [p.pelvis[0] + 1.3 * t, 0.9, p.pelvis[2] + 1.3 * t], m, "torso", {
                exponent: p.pelvisRoundness,
            }),
        )
    }

    if (look.armor) prims.push(...armorOf(sk, p, look.armor.kind, look.armor.color, t, facing))
    if (look.bauldron) {
        // Over a sleeve, if the shoulder has one; the shoulders of body armour are bare.
        const under = look.shirt && look.shirt.sleeves !== "none" ? 0.6 * t : 0
        prims.push(...bauldronOf(sk, p, rampFrom(BAULDRON_COLORS[look.bauldron.color] ?? BAULDRON_COLORS.brown), under, facing))
    }
    if (look.shield) prims.push(...shieldOf(sk, p, look.shield, facing))

    // ---- hair: a shell over the skull, cut away where the face shows ---------------------------
    // A skull has none.
    const design = look.hair && !look.bones && !look.beast && hairDesignFor(look.hair.style)
    if (look.hair && design) prims.push(...hairOf(sk, p, design, hairDye(look.hair.color)))

    // ---- held: a tool in the right fist ------------------------------------------------------
    const held = look.held && EMBER_HELD[look.held]
    if (held) prims.push(...held(sk, p, look.heldColor))

    return prims
}

/** The radius a primitive can reach from its centre, for screen-space culling. */
export const boundingSphere = (prim: Primitive): { center: Vec3; radius: number } => {
    if (prim.kind === "capsule") {
        const center = scale(add(prim.a, prim.b), 0.5)
        const half = Math.hypot(prim.a[0] - prim.b[0], prim.a[1] - prim.b[1], prim.a[2] - prim.b[2]) / 2
        return { center, radius: half + prim.r }
    }
    return { center: prim.center, radius: Math.max(prim.radii[0], prim.radii[1], prim.radii[2]) }
}
